import { parseSearchParams } from '../utils/URL'
import { SeoService } from '../services/SeoService'
import { DateTimeService } from '../services/DateTimeService'

type TranslationContext = { [key: string]: string }

interface ResourceDictionary {
    [key: string]: string | ((context: TranslationContext) => string)
}

const translations: { [key: string]: ResourceDictionary } = {
    en: {
        // routes
        'route:index': '/',
        'route:browse': (c) =>
            c['prefix']
                ? `${c['prefix']}/weekly_ad/${c['slug']}`
                : `/weekly_ad/${c['slug']}`,
        'route:item': (c) => `${c['prefix']}/item/${c['slug']}`,
        'route:coupon': (c) => `${c['prefix']}/coupon/${c['slug']}`,
        'route:coupons': (c) =>
            `${c['prefix']}/coupons${c && c['category'] ? `/${c['category']}` : ''}`,
        'route:listing': (c) =>
            `${c['prefix']}/weekly_ads${c && c['category'] ? `/${c['category']}` : ''}`,
        'route:shopping_list': '/shopping_list',
        'route:clippings': '/clippings',
        'route:install': '/install',
        'route:search': (c) => `/search/${c['q']}`,
        'route:redirect': '/redirect',
        'route:favourites': '/favorites',
        'route:home': '/home',
        'route:distribution': '/dist',
        'route:playstore': '/playstore',
        'route:appstore': '/appstore',

        // CDPv3
        cdpv3_back: (c) => `Back to ${c['site']}`,
        cdpv3_powered_by: 'Powered by',

        coupons_title: 'Coupons',
        coupons_description: (c) =>
            `We've matched coupons for your favourite brands with deals in your local ${c['city']} weekly ads.`,
        // SEO Categoy Flyers
        category_title: (c) =>
            `${c['city']} Weekly ${c['category']} Ads And Circulars | Flipp`,
        category_description: (c) =>
            `Browse the ${c['category']} weekly ads and circulars in ${c['city']}. Save by discovering products that match your budget and needs`,
        category_flyers_for_city_postal_code_category: (c) =>
            `${c['city']} ${c['category']} weekly ads and circulars`,
        category_flyers_alt: (c) => `${c['flyerName']} in ${c['city']}`,
        top_flyers: () => 'Top Weakly Ads',

        // SEO Coupon Page
        coupon_page_title: (c) => {
            if (c.discount) {
                return `Coupon: Save ${c['discount']} on ${c['store']} in ${c['city']} | Flipp`
            }

            return `Coupon: Save ${c['name']} in ${c['city']} | Flipp`
        },
        coupon_page_description: (c) => {
            if (c.discount) {
                return `Free Printable Coupon: Save ${c['discount']} on ${c['store']} in ${c['city']}`
            }

            return `Free Printable Coupon: Save ${c['name']} in ${c['city']}`
        },
        coupon_page_h1: (c) => `${c['name']} Free Coupon`,

        item_details_title: (c) => `${c['name']}`,
        item_details_description: (c) => `${c['name']} - ${c['description']}`,
        clippings_page_title: `Your weekly ad clippings`,
        clippings_page_description: `Your weekly ad clippings.`,

        // Seo Search Page Metadata
        search_page_title: (c) =>
            `Find the Best Deals for ${c['q']} in ${c['city']}, ${c['state']} | Flipp`,
        search_page_description: (c) =>
            `Save on ${c['q']} in ${c['city']} with Flipp deals and coupons. Explore all ads and discounts about ${c['q']}, browse flyers and deals in ${c['city']} and enjoy great savings.`,
        search_page_h1: (c) =>
            `Deals and Coupons for ${c['q']} in ${c['city']}`,
        search_page_h2: (c) => `${c['q']} - ${c['city']} - ${c['itemType']}`,
        search_page_h3: (c) => `${c['itemName']} - ${c['status']}`,
        search_page_alt: (c) => `${c['itemName']} Deal in ${c['city']}`,

        index_page_title: 'Weekly Ad Circulars, Deals & Online Coupons | Flipp',
        index_page_description:
            'View the latest flyers and online coupons from your favourite local stores. Find hand-picked deals, make a shopping list, and save on your weekly shopping!',
        index_page_h1: 'Save time and money weekly with digital ads on Flipp.',

        index_city_title: (c) =>
            `${c['city']} Weekly Ads, Circulars, Coupons & deals | Flipp`,
        index_city_description: (c) =>
            `View the latest weekly ads and coupons from your favourite local stores in ${c['city']}. Find deals, make a shopping list, and save on your weekly shopping!`,
        index_city_h1: (c) => `${c['city']} Weekly Ads, Coupons & Deals`,

        index_flyers_title: (c) => `${c['city']} Weekly Ads and Deals | Flipp`,
        index_flyers_description: (c) =>
            `Save with ${c['city']} weekly ads and deals. Find the best ${c['city']} deals on grocery, electronics, home improvements and more!`,
        index_flyers_h1: (c) => `${c['city']} weekly ads and deals`,
        index_flyers_alt: (c) => `${c['flyerName']} in ${c['city']}`,

        store_flyers_title: (c) =>
            `${c['store']}  Weekly Ads, Coupons, Discounts & Weekly Deals  | Flipp`,
        store_flyers_description: (c) =>
            `Browse deals and special offers in ${c['store']} with Flipp. Flyers and coupons available on website and app today! `,
        store_flyers_h1: (c) => `${c['store']}`,
        store_flyers_h2: (c) => `Top ${c['store']} Weekly Ads`,

        index_city_coupon_title: (c) =>
            `Save with Free Coupons in ${c['city']} | Flipp`,
        index_city_coupon_description: (c) =>
            `Browse free coupons and promo codes for top stores in ${c['city']}, ${c['state']}. Save on groceries, food, retail, health and household products with promo codes, coupons and loyalty cards.`,
        index_city_coupon_h1: (c) => `Save with ${c['city']} coupons`,

        index_item_title: (c) => `>Buy ${c['item']} in ${c['city']} | Flipp`,

        index_coupon_category_title: (c) =>
            `Save With Free ${c['city']} ${c['category']} Coupons | Flipp`,
        index_coupon_category_description: (c) =>
            `Browse free ${c['category']} coupons in ${c['city']}, ${c['state']}. Save by discovering products that match your budget and need`,
        index_coupon_category_h1: (c) =>
            `Save with ${c['city']} ${c['category']} coupons`,

        index_single_flyer_title: (c) =>
            `${c['name']} - ${c['city']} Circulars | Flipp`,
        index_single_flyer_description: (c) =>
            `Discover products on sale, deals and coupons at ${c['merchant']}, ${c['city']} with ${c['name']}. ${c['validity']}`,
        index_single_flyer_h1: (c) => `${c['name']}, ${c['city']}`,

        //
        delete_all: 'Delete List',
        delete_expired: 'Delete Expired',
        edit: 'Edit',
        done: 'Done',
        and: 'and',
        submit: 'Submit',

        expired: 'Expired',
        empty_clippings_title: 'You currently have no clippings',
        empty_clippings_subtitle:
            'Start browsing circulars to build your clipping list.',
        empty_clippings_body:
            'As you browse, double click on an item to circle it and add it to your clippings.',
        empty_clippings_button: 'Start Browsing',
        map: 'Map',
        clippings: 'Clippings',
        add_clip_button: 'Add to List',
        remove_clip_button: 'Remove from List',
        download_app_banner_title:
            'Use Flipp on your phone! Try the free app now.',
        store_info: 'Store Info',
        stores_for_city: (c) => `${c['city']} Stores`,
        stores: () => `Stores`,
        stores_closest_to_postal_code: (c) => `Stores near ${c['postalCode']}`,
        contest: 'Contest',
        featured_sort_help: 'Show promoted circulars',
        latest_sort_help: 'Sort circulars by publication date',
        alphabetical_sort_help: 'Sort circulars alphabetically by store',
        back_button: 'Back',
        back_to_sl: 'Back to Shopping List',
        close_dialog_button: 'Close dialog',
        open_navigation_menu: 'Open navigation menu',
        results_for_q: (c) => `Search results for '${c['q']}'`,
        results_for: 'Results for ',
        enter_your_postal_code:
            'Enter your postal code so we can find the right circulars near you.',
        x_dollars_n_cents: (c) => `${c['x']} dollars and ${c['n']} cents`,
        find_flyers: 'Find Circulars',
        x_of_n_stars: (c) => `${c['x']} out of ${c['n']} stars`,
        flyers_for_postal_code: (c) => `Circulars for ${c['postalCode']}`,
        flyers_for_postal_code_category: (c) =>
            `${c['category']} for ${c['postalCode']}`,
        flyers_for_city_postal_code_category: (c) =>
            `${c['category']} for ${c['city']} (${c['postalCode']})`,
        coupons_for_postal_code: (c) => `Coupons for ${c['postalCode']}`,
        change_location: 'Change Location',
        about_us: 'About Us',
        about_us_link: 'https://corp.flipp.com/about-us/',
        careers: 'Careers',
        keep: 'Keep',
        delete_account_confirmation:
            'Your account will be deleted soon. We will send a confirmation email shortly!',
        not_sell_data_confirmation:
            'Flipp will not sell or share your personal data. Manage your data anytime.',
        ad_blocker_detected:
            'Oops! It looks like an Ad Blocker is affecting your Flipp flyer experience. Please disable it to continue.',
        manage: 'Manage',
        item_added: 'Item added',
        item_removed: 'Item removed',
        items_added: 'items added',
        to_your_shopping_list: 'to your shopping list.',
        from_your_shopping_list: 'from your shopping list.',
        view: 'View',
        delete_account_link: 'Delete My Account',
        delete_account_button: 'Delete Account',
        delete_account_redirect: 'Sign In To Continue',
        delete_account_title: 'Are you sure you want to delete your account?',
        delete_account_text1:
            'This will permanently delete the following account:',
        delete_account_text2:
            'By confirming, we will permanently delete, aggregate, or de-identify the personal information that you have provided us with, or that we have collected about you, based on the identifiers you have given us.',
        mobile_app: 'Mobile App',
        valid_from_to: (c) => `Valid ${c['from']} – ${c['to']}`,
        flexible_validity: (c) =>
            `Valid ${c['from'] ? 'from ' + c['from'] : ''} ${c['to'] ? ' to ' + c['to'] : ''}`,
        flyers: 'Weekly Ads',
        flyer: 'Weekly Ad',
        items: 'Items',
        online: 'Online',
        online_items: 'Online Items',
        flyer_items: 'Circular Items',
        see_all_online_results: 'See all online results',
        buy_online: 'Buy Online',
        at_merchant_name: (c) => `at ${c['merchantName']}`,
        no_flyers_for_postal_code: (c) =>
            `No circulars for postal code '${c['postalCode']}'.`,
        no_flyers_for_store: (c) => `No circulars for ${c['store']}...`,
        flyers_near_you: () => 'Circulars near you',
        other_flyers_near_you: () => 'Other circulars near you',
        tombstone_msg:
            'Sorry, there are currently no circulars for this retailer.',
        featured: 'Featured',
        alphabetical: 'A-Z',
        latest: 'Latest',

        'All Flyers': 'All Circulars',
        Groceries: 'Groceries',
        'Home & Garden': 'Home & Garden',
        Electronics: 'Electronics',
        'Baby & Kids': 'Baby & Kids',
        Fashion: 'Fashion',
        Automotive: 'Automotive',
        'Sporting Goods': 'Sporting Goods',
        Pharmacy: 'Pharmacy',
        Pets: 'Pets',
        Office: 'Office',
        'Black Friday': 'Black Friday',
        'Proudly Canadian': 'Proudly Canadian',
        'Back to School': 'Back To School',
        'Boxing Day': 'Boxing Day',
        'Boxing Week': 'Boxing Week',
        'General Merchandise': 'General Merchandise',
        Specialty: 'Specialty',
        'Online Only': 'Online Only',
        Halloween: 'Halloween',
        Thanksgiving: 'Thanksgiving',
        'Cyber Monday': 'Cyber Monday',
        'Cyber Week': 'Cyber Week',
        Holidays: 'Holidays',
        'Holiday Gifting': 'Holiday Gifting',
        Christmas: 'Christmas',
        "New Year's Eve": "New Year's Eve",
        'New Years': 'New Years',
        "Valentine's Day": "Valentine's Day",
        "President's Day": "President's Day",
        Easter: 'Easter',
        "Mother's Day": "Mother's Day",
        "Father's Day": "Father's Day",
        Restaurants: 'Restaurants',

        'All Deals': 'All Deals',
        Baby: 'Baby',
        Grocery: 'Grocery',
        'Health & Beauty': 'Health & Beauty',
        Household: 'Household',
        Other: 'Other',

        previous_page: 'Previous Page',
        next_page: 'Next Page',
        next_flyer: 'Next Circular',
        zoom_in: 'Zoom In',
        zoom_out: 'Zoom Out',
        correction_notice_button: 'Show correction notices',
        correction_notice: 'Correction Notices',
        no_correction_notices:
            'There are no correction notices for this flyer.',
        starts_on_date: (c) => `Starts ${c['date']}`,
        starts_on_day: (c) => `Starts ${c['day']}`,
        starts_tomorrow: 'Starts Tomorrow',
        starts_today: 'Starts Today',
        n_years_left: (c) => `${c['n']} Years Left`,
        '1_year_left': '1 Year Left',
        n_months_left: (c) => `${c['n']} Months Left`,
        '1_month_left': '1 Month Left',
        n_weeks_left: (c) => `${c['n']} Weeks Left`,
        '1_week_left': '1 Week Left',
        until_day: (c) => `Until ${c['day']}`,
        until_tomorrow: 'Until Tomorrow',
        ends_today: 'Ends Today',
        ended_on_date: (c) => `Ended ${c['date']}`,
        no_search_results: (c) => `Couldn't find any results for '${c['q']}'.`,
        search: 'Search',
        search_placeholder: 'Search circulars or items',
        search_placeholder_mobile: 'Search',
        search_category_all: 'All',
        search_category_flyers: 'Circulars',
        search_category_items: 'Items',
        search_category_coupons: 'Coupons',
        search_category_online: 'Online',
        postal_code: 'Postal Code',
        legal: 'Legal',
        company: 'Company',
        accept_terms: 'I have read and accept the ',
        accept_privacy: 'I have read and accept the ',
        terms_url: 'https://corp.flipp.com/legal/terms_of_use',
        privacy_url: 'https://corp.flipp.com/legal/privacy',
        terms_of_use: 'Terms of Use',
        privacy_policy: 'Privacy Policy',
        or: 'or',

        my_data: 'Request Your Data',
        my_data_title: 'Request your data',
        my_data_select_country: 'Select your country',
        my_data_select_state: 'Choose your state or province',
        my_data_select_option: 'Select an option',
        my_data_resident: 'I am resident of:',
        my_data_name: 'First Name (required)',
        my_data_surname: 'Last Name (required)',
        my_data_email: 'Email (required)',
        my_data_name_error: 'Please enter your first name',
        my_data_surname_error: 'Please enter your last name',
        my_data_email_error: 'Please enter a valid email address',
        my_data_california: 'California, USA',
        my_data_colorado: 'Colorado, USA',
        my_data_connecticut: 'Connecticut, USA',
        my_data_virginia: 'Virginia, USA',
        my_data_quebec: 'Quebec, CA',
        my_data_select_request: 'Select your data request below',
        my_data_select_option_label_do_not_sell: 'Do not sell my data',
        my_data_select_option_label_receive_copy: 'Receive a copy of my data',
        my_data_select_option_label_delete_data: 'Permanently delete my data',
        my_data_select_option_text_do_not_sell:
            'We will not sell the personal information that you have provided us with, or that we have collected about you, to third parties.',
        my_data_select_option_text_receive_copy:
            'We will send you a copy of the personal information that you have provided us with, or that we have collected about you, based on the identifiers you have given us.',
        my_data_select_option_text_delete_data:
            'We will delete, aggregate, or de-identify the personal information that you have provided us with, or that we have collected about you, based on the identifiers you have given us.',
        my_data_text:
            'You have specific rights with respect to your data in accordance with applicable law. Please fill out the fields below and submit if you would like to exercise one of those rights. For more information please see our privacy policy.',
        my_data_disclaimer_text1:
            'If you have any questions or concerns with respect to this form or your rights, please review our privacy policy available at ',
        my_data_disclaimer_text2:
            'We are required to verify your identity before responding to any request for information or request to delete, and will do so by matching the personal information you provide in this form with the information contained in our systems.',
        my_data_disclaimer_text3:
            'If you would like to know the categories of information Flipp collects with respect to all users, please refer to the privacy policy.',
        my_data_delete_modal_title: 'Permanently delete your Flipp data?',
        my_data_delete_modal_confirm: 'Request Deletion',
        my_data_confirm_success_header: 'Request submitted successfully',
        my_data_confirm_success_text1:
            'Your request was submitted successfully.',
        my_data_confirm_success_text2: 'We will send you an email shortly.',
        my_data_confirm_success: 'Request submitted successfully',
        my_data_error_modal_title: 'Error',
        my_data_error_modal_text: 'Sign In To Submit',
        my_data_delete_modal_text:
            'Note that this process is not reversible and you will permanently lose any personalization or\n' +
            '\t\t\t\t\tcustomization of your experience.',
        my_data_server_error_message:
            'An internal error occurred. Our team is working to resolve it. Please try again later.',
        my_data_nonetwork_error_message:
            'Something went wrong. Check your internet connection and try again.',

        patents: 'Patents & TM',
        reviews: 'Reviews',
        overall_rating: 'Overall Rating',
        based_on_x_reviews: (c) => `Based on ${c['x']} reviews`,
        review_by_author: (c) => `Review by ${c['author']}`,
        x_positive_reviews: (c) => `${c['x']} positive reviews`,
        x_negative_reviews: (c) => `${c['x']} negative reviews`,
        x_from_y_price: (c) => `${c['x']} from ${c['y']} for ${c['price']}`,
        was: 'Was ',
        in_store_only: 'In store only',
        description: 'Description',
        features: 'Features',
        specs: 'Specifications',
        item_disclaimer:
            'In the event of disagreement between the circular and this popup, the circular shall take precedence.',
        ecom_disclaimer:
            "Prices are provided to us by merchants, and subject to updates such that the prices displayed may not be current. In the event of discrepancies, the merchant's website shall take precedence.",
        leaving_flipp: 'Leaving Flipp.',
        click_to_visit_m: (c) => `Click to visit ${c['m']}.`,
        matching_coupons: 'Matching coupons',
        save: 'Save',
        print: 'Print',
        print_coupon: 'Print Coupon',
        print_list: 'Print List',
        mobile_install_headline: 'Download or open the Flipp app to',
        mobile_install_subline: 'save more on your favourite brands',
        mobile_install_coupons:
            'Find thousands of digital coupons to enjoy instant savings at checkout',
        mobile_install_clippings:
            'Easily clip items to your list with a single tap and stay organized as you plan your shopping',
        xbrowse_more_flyers: 'More Circulars',
        xbrowse_see_all_flyers: 'See All Circulars',
        page_thumb: (c) => `Page ${c['start']} of ${c['end']}`,
        my_location_title: 'My Location',
        postal_code_tip: 'There is no content available for this location',
        my_location_desc:
            'Enter your zip code so we can find the right flyers near you.',
        coupon_unavailable_cta: 'Sorry, this coupon is no longer available',
        coupon_unavailable_limit_message:
            'This coupon has reached the maximum limit of redemptions. Please continue browsing for other coupons.',
        coupon_unavailable_fsa_message:
            'This coupon is not available in your area. Please continue browsing for other coupons.',
        you_save: 'You save',
        percent_off: (c) => `${c['percent']}% off`,
        feedback_text: '“Loving this app! Great for people on a budget!”',
        feedback_text_1:
            '“I use Flipp almost daily. So user friendly and  lets me find the best deals for the things I need.”',
        feedback_text_2:
            '“Flipp allows me to shop all sale items from every store.”',
        feedback_text_3:
            '“Very great app!! Super easy to use, tons of flyers and a great place to make a grocery list.”',
        feedback_text_4: '“Easy to navigate and always up to date”',
        feedback_text_5: '“Love that everything is in one place. Easy to use.”',
        feedback_text_6: '“This app helps me comparison shop like a boss!”',
        feedback_text_7:
            '“Search what you’re looking for. Find the best price and where to buy it.”',
        feedback_text_8:
            '“Amazing app, used for years, always a go to before grocery shopping.”',
        feedback_text_9: '“This is a great app! Very handy and easy to use.”',
        feedback_text_10: '“Best thing to happen for a busy, thrifty mom!”',
        feedback_text_11: '“Love this app and I save a lot of money using it!”',
        mobile_banner:
            'Find the best deals on everything you’re looking for. Try Watch List on Flipp.',
        mobile_banner_blackfriday:
            'Stay on top this Black Friday with Watch List. Only on the Flipp app.',
        get_app: 'Get App',
        mobile_popup_banner:
            'View this weekly ad in the Flipp app for the best reading experience.',
        continue_in_web_browser: 'Continue in web browser',

        flyersCountry_title: 'USA Weekly Ads Collection',
        storeCityFlyers_title: 'Browse Store Weekly Ads by City',
        flyersCountry_content1:
            'With our collection of USA weekly ads, Flipp brings you all the latest info on special promotions, sales, discounts and deals from retailers across USA.',
        flyersCountry_content2:
            'Helping you shop smarter, Flipp is always up to date with the latest weekly ads information. Every time new weekly ads go into circulation, Flipp helps you find the best ways to save money on virtually any purchase. Whether you are shopping for clothing, furniture, groceries, toys, food, school supplies, or home decor, Flipp makes it easy to find what you need at the best price.',
        flyersCountry_content3:
            'Our collection of weekly ads from top USA stores, including Aldi, Family Dollar, Target, Dollar General, Publix, Home Depot and more, brings the bargains to you! All the weekly ads are available on our website, but if you want to have the discounts at your fingertips, download the Flipp app today.',
        flyersCountry_content4_a: 'Available for ',
        flyersCountry_content4_or: 'or',
        flyersCountry_content4_b:
            ' devices, the Flipp app is one of the most powerful savings tools you can find. Bringing together everything from electronics weekly ads to grocery coupons, the Flipp app lets you quickly find and compare prices from store to store, stay organized with a single shopping list, and when you sign in, your account automatically syncs across devices, so you never have to worry about losing track of the biggest savings.',
        flyersCountry_content5:
            'No matter where you are in USA, weekly ads and coupons are a great way to keep more cash in your pocket. Leaving no postal code unserved, our collection of USA weekly ads includes businesses from USA cities of all sizes. ',
        flyersCountry_top10: 'Top 10 Cities - Local Weekly Ads',
        flyersCountry_footnote:
            'Note: All logos and trademarks are property of their respective owners',
        flyersCountry_all: 'Browse Weekly Ads From Across USA',

        // Desktop Banner
        desktop_banner_title: 'Introducing Watch List! Only on the Flipp app.',
        desktop_banner_body:
            'Create a list of deals to easily monitor, and receive alerts when an item goes on sale. Enter your phone number or scan the QR code with your device to receive a download link.',
        desktop_banner_submit: 'Text me the app',
        desktop_banner_footer:
            '*By entering phone number, you consent to the use of your phone number to send you a link to the app, and to the use of your phone number in accordance with the app’s ',
        desktop_banner_footer_2:
            '. standard rates apply. Flipp will not share your phone number with any third party.',
        desktop_banner_privacy_policy: 'privacy policy',
        desktop_banner_placeholder: 'Enter phone number',
        'desktop-banner_error':
            'Invalid number. Please check for mistakes and try again.',
        'desktop-banner_success_title': 'Message sent!',
        'desktop-banner_success_body': (c) =>
            `We’ve just sent a text message to +1 ${c.number} with your download link. Please check your messages and follow the instructions to download the app.`,

        // Black Friday Page
        view_more: 'More Tips',
        blackfriday_blog_title: 'Blogs',
        blackfriday_title: () => `Black Friday Flyers and Deals | Flipp`,
        blackfriday_description: () =>
            `Save with Black Friday Flyers and Deals. Find the best Black Friday deals on grocery, electronics, home improvements and TV!`,
        blackfriday_deals: 'See Black Friday Deals',
        cyberweek_deals: 'See Cyber Week Deals',
        blackfriday_banner: 'Black Friday Deals and Coupons on Flipp',
        blackfriday_banner_title1: 'Snag your ultimate',
        blackfriday_banner_title2: 'Black Friday deals',
        cyberweek_banner_title: 'Flipp: Cyber Week Deals are Here!',
        blackfriday_banner_subtitle:
            'Find the hottest deals from your favorite stores',
        blackfriday_home_banner: 'Black Friday Deals and Coupons on Flipp',
        blackfriday_home_wording: 'The New Black Friday is Now!',
        no_flyers_for_blackfriday_page:
            'Black Friday Weekly Ads are coming soon.',
        giveaway_title: 'Black Friday Giveaway',
        giveaway_banner_title: 'Black Friday Giveaway',
        giveaway_banner_subtitle: 'Daily prizes to be won!',
        giveaway_banner_date: 'November 18 – December 6, 2024',
        giveaway_banner_button_text: 'Enter Today’s Giveaway',
        giveaway_christmas_title: 'Days of Christmas Giveaway',
        giveaway_christmas_subtitle: 'Daily prizes worth $500',
        giveaway_christmas_button_text: 'Enter now',

        giveaway_back_to_school_title1: 'Back-to-School',
        giveaway_back_to_school_title2: 'Giveaway',
        giveaway_back_to_school_date: 'August 21–24, 2024',
        giveaway_back_to_school_description:
            'Up to $1,500 USD in prizes to be won!',

        // Proudly Canadian deals
        proudly_canadian_banner_title:
            'Save at Your Favourite Canadian-Owned Stores',
        proudly_canadian_banner_title1: 'Save at Your Favourite',
        proudly_canadian_banner_title2: 'Canadian-Owned Stores',
        proudly_canadian_deals: 'Find Deals Now',
        proudly_canadian_disclaimer:
            "We aim to feature Canadian-owned businesses or products made in Canada based on trusted third-party data. However, we haven't independently verified ownership and can't guarantee accuracy. We encourage conducting your own research.",

        // Watchlist Page
        watchlist_page_title: 'Watch List | Flipp',
        watchlist_title: 'Watch List is only available on the Flipp app',
        watchlist_headline:
            "Watch prices drop with Flipp's Watch List. Only on the Flipp app.",
        watchlist_button: 'Download App',

        // Landing Page strings ---------->
        download_btn: 'Download Flipp',
        about_flipp: 'About Flipp',
        give_feedback: 'Give feedback, win swag',
        flipp_mobile: 'Flipp Mobile App',
        landing_page_header: 'Flipp for the most savings every week',
        landing_page_description:
            'Flipp makes weekly shopping easy with the most digital circulars and coupons from the brands you love',
        enter_postal_code_error_message: 'Please enter a valid zip code.',
        find_weekly_ads: 'Find your weekly circulars for',
        browse_flipp: 'Browse Flipp',
        enter_postal_code:
            "Enter your zip code and we'll find weekly circulars in your area",
        how_it_works: 'How does it work?',
        browse: 'Browse',
        browse_description: 'Browse through hundreds of your weekly circulars',
        search_description: 'Find a specific store, item or brand with ease',
        clip: 'Clip',
        clip_description: 'Tag an item to clip it and stay organized',
        flyers_found: (c) =>
            `We've found you ${c['num']} local weekly circulars from your favorite retailers`,
        browse_flyer: 'Select a circulars to start browsing',
        more_flyers: (c) => `See ${c['num']} more circulars in ${c['city']}`,
        all_flyers: (c) => `See all circulars in ${c['city']}`,
        search_value:
            '{#1}Search{\\1} for items, brands, and stores to quickly find what you need.',
        find_deals_for: 'Find me the best deals for',
        search_flipp: 'Search Flipp',
        has_mobile: 'We have a mobile app',
        mobile_title:
            'Always have your savings at your fingertips with the Flipp mobile app for iOS and Android',
        mobile_title_new: 'Save on-the-go with the {#1}Flipp app.{\\1}',
        mobile_value:
            'Add your loyalty cards, sync your shopping list across devices, and share your list with your shopping partner  — only on the app!',
        mobile_feature1:
            '{#1}Seamlessly sync{\\1} your shopping list and share.',
        mobile_feature2: '{#1}Navigate the store{\\1} like a pro.',
        mobile_feature3:
            '{#1}Add your loyalty cards{\\1} for savings at checkout.',
        learn_more: 'Learn More',
        landing_page_title: 'Circulars, Coupons and Shopping List',
        deal_search: 'Enter an item',
        deal_search_desktop: 'Try searching for ‘eggs’',
        deal_sample1: 'Cheese',
        deal_sample2: 'Diapers',
        deal_sample3: 'Yogurt',
        deal_sample4: 'Butter',
        deal_sample5: 'Chicken',
        read_weekly_ad: 'Read Weekly Ad',
        coupons: 'Coupons',
        coupon: 'Coupons',
        coupon_heading_can: "We've made saving even easier",
        coupon_sub_heading_can:
            'Sign in to your Flipp account to save and sync your loyalty cards. {#1}Download{\\1} the Flipp App to get them on the go.',
        coupon_heading_usa: 'Save more with loyalty card coupons',
        coupon_sub_heading_usa:
            'Sign in to your Flipp account to save and sync your loyalty cards, and clip select coupons',
        coupon_category_notice: (c) => `${i18n(c['category'])} Coupons`,
        available_at: 'Available at',
        see_in_weekly_ad: 'See it in the weekly ad',
        matchup_at: 'Matchup at',
        regular_price: 'Regular Price',
        sale_price: 'Sale',
        current_price: 'Current Price',
        coupon_price: 'Coupon',
        final_price: 'Get it for',
        coupon_details_title: 'Coupon Details',
        no_coupons_for_postal_code: (c) =>
            `No coupons for postal code '${c['postal_code']}'.`,

        // New Landing Page strings ---------->
        back_to_top: 'Return to top of the page',
        flyers_header_category: 'Weekly Ads',
        start_saving: 'Start Saving',
        new_landing_page_description:
            '{#1}Start saving.{\\1}Flipp has everything you need to save each week.',
        how_to_save_feature1:
            '{#1}Type your zip code{\\1} below to see the latest deals near you.',
        how_to_save_feature2: '{#1}100+ coupons{\\1} from brands you love',
        how_to_save_feature3:
            '{#1}The ultimate shopping list{\\1} to help you save',
        flyers_feature_heading:
            'Save at over 2,000 of your {#1}favourite stores.{\\1}',
        flyers_feature_description1:
            'Flipp is your one-stop marketplace for savings and deals. Browse weekly ads from retailers near you, or search for the items you need.',
        flyers_feature_description2_part1:
            'For the best mobile experience and to browse products on-the-go, download the ',
        flyers_feature_description2_part2:
            ' today. Available on the App Store, Google Play.',
        flipp_app_title: 'Flipp App',
        flyers_feature_cta: 'Browse Weekly Ads',
        coupons_feature_heading:
            'Add coupons directly to your {#1}loyalty cards.{\\1}',
        coupons_feature_description1:
            'With Flipp, there’s no need to carry around all your loyalty cards. Simply add your cards to the Flipp app and clip your coupons digitally to save in-store.',
        coupons_feature_description2_part1: 'Download the ',
        coupons_feature_description2_part2:
            ' to find and clip coupons on-the-go.',
        coupons_feature_cta: 'Browse Coupons',
        sl_feature_heading:
            'Stay organized with your {#1}digital Shopping List.{\\1}',
        sl_feature_description_part1:
            'Clip deals directly to your shopping list or add your own items. Flipp will instantly find deals for the items on your list. Download the ',
        sl_feature_description_part2:
            ' to have your list on hand while you shop.',
        sl_feature_cta: 'Create a Shopping List',

        // Error Page strings ---------->
        missing_page_title: 'Clean up on aisle 404!',
        missing_page_apology_msg:
            'Sorry about the mess. The page you were trying to reach could not be found or is not available in your area.',
        missing_page_cta_msg:
            "While we're cleaning up, please continue browsing our latest circulars.",
        missing_page_cta_button: 'Start Browsing Circulars',

        // Footer strings ---------->
        categories: 'Categories',
        locations: 'Locations',
        retail_platform: 'Retail Platform',
        media_platform: 'Media Platform',
        contact_us: 'Contact Us',
        help: 'FAQ/Help',
        help_link: 'https://help.flipp.com/hc/en-ca',
        client_login: 'Client Login',
        terms: 'Terms of Use',
        accessibility: 'Accessibility Plan',
        ad_choices: 'Ad Choices',
        hiring: "We're Hiring!",
        blog_desc:
            'For shopping tips, everyday living, and ideas on a budget, visit the Flipp Blog today.',
        read_blog: 'Read Blog',
        blog_button: 'Blog',
        copyright: (c) =>
            `© Copyright ${c['year']} Flipp Corp. All rights reserved.`,
        webgl_warning_msg:
            "Flipp uses a standard technology called WebGL to provide an extraordinary viewing experience. We noticed that you currently don't have it enabled.",
        webgl_warning_cta:
            'Enable WebGL now to have the best viewing experience possible, or you can continue viewing the circular as is.',
        webgl_warning_click_here: 'Click here',
        webgl_warning_learn: ' to learn how to enable WebGL.',
        old_browser_warning_msg: 'Uh oh, your browser is out of date!',
        old_browser_latest_tech_msg:
            'We built Flipp using the latest technology to make it faster and easy to use.',
        old_browser_cta:
            'Please download one of these up-to-date, free browsers for the best experience.',
        runtime_error_title:
            'Uh oh, an unexpected error seems to have occurred.',
        runtime_error_msg:
            "Please try refreshing the page or you can <a href='https://flipp.force.com/Community_HomePage'>contact us</a> if the problem persists.",

        // Autocomplete strings ----->
        autocomplete_separator_merchants: 'Merchants',
        autocomplete_separator_items: 'Items',
        autocomplete_flyer: '1 circular available',
        autocomplete_flyers: (c) => `${c['num']} circulars available`,

        // Accounts strings ------>
        signin: 'Sign in',
        continue_with_email: 'Continue with Email',
        continue_with_google: 'Continue with Google',
        continue_with_facebook: 'Continue with Facebook',
        signin_action: 'Sign in',
        signin_header: 'Sign In',
        signin_no_account: "Don't have an account?",
        signin_resend_verification_code: 'Resend verification code',
        signup: 'Sign up',
        signup_header: 'Sign Up',
        signup_have_account: 'Already have an account?',
        enter_email_error: 'Please enter your email',
        enter_invalid_email_error: 'Invalid email format',
        enter_password_error: 'Please enter your password',
        enter_previous_password_error: 'Please enter your previous password',
        USER_NOT_CONFIRMED: 'User is not confirmed', // override backend error message
        EMAIL_EXISTS: 'This email is already being used', // override backend error message
        enter_confirmation_code_error: 'Please enter confirmation code',
        confirm_password_error: 'Please confirm your password',
        passwords_mismatch_error: 'Passwords must match',
        firstname: 'First Name',
        password: 'Password',
        new_password: 'New Password',
        confirm_password: 'Confirm Password',
        forgot_my_password: ' Forgot My Password',
        continue_with: 'or continue with',
        forgot_password_header: 'Reset your password',
        forgot_password_text:
            'Please enter your email to receive a code to reset a password',
        forgot_password_send_code: 'Send me the code',
        confirm_email_header: 'Please verify your email',
        confirm_email_resend_header: 'Please check your email',
        confirm_email_text:
            'In order to verify your account, please enter your verification code below.',
        confirm_email_resend_text:
            'Enter the verification code sent to your email to finish creating your Flipp account.',
        confirm_email_code: 'Verification code',
        confirm_email_button_text: 'Continue',
        confirm_email_under_button_text:
            'Didn’t receive a verification code? Please check your spam or junk mail folder',
        confirm_email_under_button_action: 'Resend',
        maybe_later: 'Maybe later',
        confirm_change_password_header: 'Please check your email',
        confirm_change_password_text:
            'In order to reset your password, please enter your verification code below.',
        change_password: 'Change Password',
        change_password_header: 'Change Your Password',
        change_password_text: '',
        change_password_button_text: 'Confirm',
        previous_password: 'Current password',
        opt_in_header: 'Great deals sent straight to your inbox',
        opt_in_text:
            'We’ll send you updates and deals from stores near you. By signing up for Flipp emails, you agree to our terms & conditions. You can opt out at any time. For more information view our {#1}privacy policy{\\1} or {#2}contact us{\\2}.',
        opt_in_button_text: 'Subscribe to emails',
        no_thanks: 'No thanks',
        signout: 'Sign out',
        signin_title: 'Create an account on',
        signin_title_form: 'In order to manage your data please sign in',
        signin_value:
            'Creating an account will let you save your shopping list, coupons and loyalty cards and have them available wherever you sign in.',
        signin_legal:
            'By signing in, you agree to our {#1}Terms of Service{\\1} & {#2}Privacy Policy{\\2}',
        signin_success: 'You have successfully signed in.',
        signin_email_missing:
            'We require your email address to log in, please allow Facebook to provide it.',
        signin_value_form:
            "If you are a California resident, please sign-in with your Flipp Account to submit a request pursuant to the California Consumer Privacy Act. If you do not have a Flipp Account, Flipp does not collect sufficient personal information from you to verify a request under the CCPA. Please see Flipp's Privacy Policy for more information.",
        stop_all_the_posting: 'Flipp will never post anything on your behalf.',
        name: 'Name',
        email: 'Email',
        email_signup: 'Get the best deals sent to your inbox',
        email_signup_prop: 'By subscribing to Flipp emails you agree to our ',
        email_signup_terms: 'terms & conditions',
        email_signup_prop_2:
            '. You can opt out at any time. For more information, view our ',
        email_signup_privacy: 'privacy policy',
        email_signup_contact: ' contact us',
        email_decline: 'Decline',
        email_accept: 'Accept',
        signin_error:
            'There was an error signing in to your account. Please verify your credentials and try again.',
        please: 'please ',

        // Load to card --------->
        my_cards: 'My Loyalty Cards',
        add_card: 'Add a Loyalty Card',
        card_category_all: 'All Coupons',
        card_category_card: 'Loyalty Card',
        card_category_print: 'Print',
        cancel: 'Cancel',
        card_number: 'Card Number',
        phone_number: 'Phone Number',
        enter_card_number: 'Enter card number',
        enter_phone_number: 'Enter phone number',
        enter_pin: 'Enter PIN',
        one_coupon_available: '1 coupon available',
        add_card_invalid: 'Card number is not valid. Please try again.',
        add_pin_invalid: 'PIN number is not valid. Please try again.',
        add_phone_invalid: 'Phone number is not valid. Please try again.',
        multi_coupon_available: (c) => `${c['num']} coupons available`,
        remove_card: 'Remove Card',
        remove_card_text: 'Are you sure you want to remove this card?',
        remove_card_yes: 'Yes',
        removing: 'Removing...',
        no_coupons_clipped: 'No coupons have been clipped to this card',
        clipped: 'Clipped',
        redeemed: 'Redeemed',
        clip_cta: 'Clip directly to a Loyalty Card',
        add_card_login_alert: 'You must sign in before you can clip a coupon',
        add_card_alert: (c) =>
            `You need to add your ${c['name']} loyalty card before you can clip this coupon`,
        clipping_error: 'Failed to clip the coupon to your card.',
        add_card_success: (c) =>
            `Successfully added your ${c['name']} loyalty card`,
        add_card_error: 'Failed to add your loyalty card.',
        search_cards: 'Search for loyalty cards',
        remove_card_error: 'Failed to remove your loyalty card',
        print_coupons: 'Print Coupons',
        clipping_help_msg: 'Clipping / Redemption Help',
        unavailable: 'Unavailable',
        unavailable_message: (c) =>
            `Your ${c['card']} card is currently unavailable. Please try again later.`,
        coupons_category: (c) => `${c['name']} Coupons`,
        show_more: 'Show More',
        no_loyalty_programs: 'No loyalty cards found',
        postcard_error_11: 'The provided card number is not valid.',
        postcard_error_12: 'User is already registered with the merchant.',
        postcard_error_13:
            'The card is already registered under a different banner.',
        postcard_error_21:
            'Coupon already clipped to a different store banner.',

        // Shopping List --------->
        sl_share_accept_header: (c) =>
            `${c['name']} has invited you to share shopping lists`,
        sl_share_accept_footer:
            'Collaborate on your shopping lists in real time, get organized and start saving more.',
        sl_share_sign_in: 'Sign in to combine our lists together',
        sl_share_no_thanks: 'No Thanks',
        sl_share_combine: 'Combine our Lists Together',
        sl_share_combining: 'Combining lists...',
        sl_share_combine_complete: (c) =>
            `You and ${c['name']} are now sharing a shopping list`,
        sl_share_combine_cancel:
            'Your shopping list items and clippings will no longer be combined.',
        sl_disconnect_msg: 'Disconnect Shopping List',
        sl_share_view_list:
            'Open your shared shopping list to start collaborating in real time.',
        sl_share_error_title: 'The share request is invalid or expired',
        sl_share_error_desc:
            'Please ask the sender to share their shopping list again.',
        sl_share_error_self_title:
            'You cannot share shopping lists with yourself',
        sl_share_error_self_desc:
            "Make sure you're logged in with a different account to accept this share request.",
        sl_share_error_invite_accepted_title:
            "You've already accepted this invite",
        sl_share_error_invite_accepted_desc:
            'You are already collaborating on a shopping list. Download our mobile app to get started.',
        sl_share_error_already_shared_title: 'Shopping list already shared',
        sl_share_error_already_shared_desc:
            'This shopping list already has the maximum number of collaborators.',

        shopping_list: 'Shopping List',
        shopping_list_shared: 'Shared Shopping List',
        shopping_list_title: 'Your Shopping List - Flipp',
        shopping_list_cta: 'Check out our new shopping list!',
        redirect_coupon_message:
            'The coupon you were looking for is expired. We have you redirected you to the relevant coupons from your city. We apologize for the inconvenience',
        redirect_flyer_message:
            'You have been redirected to the latest version of the flyer',
        redirect_item_message:
            'This item is expired. You have been redirected to the latest version of the flyer',
        redirect_to_store_message:
            'This item is expired. You have been redirected to the relevant store flyers',
        redirect_to_flyers_message:
            'This item is expired. You have been redirected to the latest flyers',
        enter_item: 'Add an item',
        zero_state_login_title: 'Save your shopping list',
        zero_state_login_desc:
            'Sign in to save your shopping list and have it available anywhere you go.',
        popular_items_header: 'Popular Items',
        one_week_ago_items_header: 'Added 1 week ago',
        two_weeks_ago_items_header: 'Added 2 weeks ago',
        recommended_items_header: 'Recommended items',
        add_all: 'Add All',
        no_more_suggestions: "You've added all our suggestions.",
        no_more_suggestions_cta:
            'Check back next week for more recommendations.',
        no_clippings: 'No items added yet',
        no_items: 'No Items Yet',
        no_clippings_message:
            "Items you've added from flyers and your shopping list will be saved here for easy access",
        deal: 'Deal',
        deals: 'Deals',
        clean_up: 'Clean Up List',
        clean_up_title: 'Clean up Shopping List?',
        clean_up_desc:
            'Cleaning up your list will delete checked and expired items.',
        clean_up_complete:
            'Your shopping list has been successfully cleaned up.',
        delete_list_title: 'Delete Shopping List?',
        delete_list_desc:
            'All of your shopping list items will be deleted but your clippings will not be deleted.',
        delete_list: 'Delete List',
        shopping_list_starter_full:
            'Start building your shopping list either by typing an item or clicking on one of these items',
        shopping_list_starter_part1:
            'Start building your shopping list either by',
        shopping_list_starter_part2: ' typing an item',
        shopping_list_starter_part3: ' or',
        shopping_list_starter_part4: ' clicking on one of these items',
        your_clippings: 'Your Item Clippings',
        no_items_found: 'Sorry, we were unable to find results for your item.',
        item_count_single: 'item',
        item_count_multi: 'items',
        flipp_to_go: 'Take Flipp to go',
        flipp_to_go_desc:
            'Create your shopping list from the comfort of your home and bring it with you to the store.',
        share_list_title: 'Share your shopping list',
        share_list_desc:
            'Combine your list with a friend and collaborate on a single shopping list.',
        share_list_cta: 'Share Invite Link',
        sharing_sl_msg: 'Collaborating on shopping list',
        start_sharing_sl_msg: 'Collaborate on shopping list',
        stop_sharing_sl_msg: 'Stop collaborating?',
        invite_dialog_title: 'Start collaborating now',
        invite_dialog_desc:
            'Send your private link to a friend and once they accept your invitation, your shopping list will be combined together.',
        invite_dialog_copy: 'Copy this private link to share',
        invite_dialog_sharing: 'Generating invite...',
        unshare: 'Unshare',
        unshare_dialog_title:
            'Are you sure you want to unshare your shopping list?',
        unshare_dialog_desc:
            "You won't be able to collaborate on your shopping list and clippings.",
        unshare_dialog_action: 'Unshare Shopping List',
        unshare_dialog_message: 'Unsharing shopping list...',
        shopping_list_save_error:
            'There was an error saving your shopping list changes. Please try again.',
        shopping_list_exceeded_limit_error:
            'Your list cannot be saved because you have exceeded the maximum number of items.',
        shopping_list_blocked:
            'Download the Flipp App to unlock shopping list & more',
        shopping_list_blocked_bf:
            'Stay on top this Black Friday with Watch List. Only on the Flipp app.',
        shopping_list_blocked_dist:
            'Visit on desktop to start building your shopping list.',
        embedded_shopping_list_title: 'Your Flipp Shopping List',
        embedded_shopping_list_desc:
            'Add items to your list and sync it with your Flipp app. Never forget an item again!',

        category_my_clippings: 'My List',
        category_other: 'Other',
        category_photo_lists: 'Photo Lists',

        // Quick Adds -------------->
        item_apple: 'Apple',
        item_bacon: 'Bacon',
        item_bananas: 'Bananas',
        item_bread: 'Bread',
        item_broccoli: 'Broccoli',
        item_butter: 'Butter',
        item_cereal: 'Cereal',
        item_cheese: 'Cheese',
        item_chicken: 'Chicken',
        item_cucumber: 'Cucumber',
        item_eggs: 'Eggs',
        item_grapes: 'Grapes',
        item_yogurt: 'Yogurt',
        item_lettuce: 'Lettuce',
        item_milk: 'Milk',
        item_orange_juice: 'Orange Juice',
        item_strawberry: 'Strawberry',
        item_toilet_paper: 'Toilet Paper',
        item_tomato: 'Tomato',
        item_water: 'Water',

        // Favourites --------->
        fav_page: 'Favorites',
        fav_title: 'Favorites - Flipp',
        fav_h2: 'You currently do not have any favorite stores.',
        fav_desc:
            'Click the [HEART] on your favourite stores to see their circulars in your favourites.',

        // Common strings --------->
        ok: 'OK',
        ok_got_it: 'OK, Got It',
        got_it: 'Got it',
        copy: 'Copy',
        session_expired_title: 'Your session has expired',
        session_expired_desc: 'Please sign in to continue.',

        // P&G Everyday BrandSaver strings --------->
        // FIXME: Separate out P&G Everyday strings
        // See FW-1325: Create separate i18n bundles for flipp.com and P&G Everyday
        pg_my_location: 'My Location',
        pg_enter_zip: 'Please enter your ZIP code.',
        pg_invalid_zip: 'Please enter a valid ZIP code',
        pg_redemption_title: 'Ways to Redeem this Coupon',
        pg_redeem_l2c: 'Add it to your shopper card',
        pg_redeem_l2c_desc:
            'Select a shopper card to clip this coupon and use it at checkout',
        pg_redeem_rebate: 'Take a picture of your receipt',
        pg_redeem_rebate_desc:
            'Download the Flipp App to redeem this coupon in 3 easy steps',
        pg_redeem_print: 'Print this coupon',
        pg_redeem_print_desc:
            'Add this coupon to your basket and print it to redeem at store',
        pg_add_card_desc: 'Add your shopper card to clip this coupon.',
        pg_add_card: 'Add card',
        pg_delete_card_message: 'Are you sure you want to delete this card?',
        pg_delete_card: 'Delete Card',
        pg_delete_confirm: 'Yes, Delete it',
        pg_deleting: 'Deleting card...',
        pg_cancel: 'Cancel',
        pg_clip_success:
            'This coupon has been successfully added to your shopper card.',
        pg_added_success: (c) => `Your ${c['name']} card has been added.`,
        pg_added_error: (c) =>
            `There was an error adding your ${c['name']} card.`,
        pg_delete_success: (c) => `Your ${c['name']} card has been deleted.`,
        pg_delete_error: (c) =>
            `There was an error deleting your ${c['name']} card.`,
        pg_clip_error:
            'We were unable to clip the coupon at this time. Please try again later.',
        pg_redeem_help_title: 'How to use this Rebate Coupon',
        pg_redeem_help_desc:
            'Flipp is a free mobile app that makes weekly shopping easy with the most digital circulars and coupons from the brands you love.',
        pg_redeem_help_cta:
            'Browse, clip and redeem offers in the palm of your hands.',
        pg_redeem_help_back: 'Back to Coupon',
        pg_basket_add: 'Add to basket',
        pg_basket_remove: 'Remove from Basket',
        pg_coupon_unavailable:
            'Sorry, this coupon is not available in your area.',
        pg_coupon_unavailable_desc:
            'There are no retailers in your area where this brandSAVER coupon can be used. Please continue browsing for more coupons.',
        pg_back_to_brandsaver: 'Back to brandSAVER',
        pg_coupon_printed:
            'Each coupon can only be printed once, and it seems you have already printed this one.',
        pg_coupon_area:
            'There is no retailer shopper card available in your area that this brandSAVER coupon can be added to.',
        pg_dialog_close: 'Close',
        pg_old_browser_latest_tech_msg:
            'We built brandSAVER using the latest technology to make it faster and easy to use.',
        pg_webgl_warning_msg:
            "brandSAVER uses a standard technology called WebGL to provide an extraordinary viewing experience. We noticed that you currently don't have it enabled.",
        pg_runtime_error_msg:
            "Please try refreshing the page or you can <a href='https://pgconsumersupport.secure.force.com/ContactUs/?brand=PG+Everyday&country=United+States+of+America&language=English-US' target='_blank'>contact us</a> if the problem persists.",
        pg_coupon_help: 'Redemption help',
        pg_card_slider_page: (c) => `Page ${c['current']} of ${c['total']}`,
        pg_card_slider_goto_page: (c) => `Go to page ${c['num']}`,

        // Breadcrumbs
        br_home: 'USA',

        // Expiread Metadata Flyer
        expired_flyer_title: (c) =>
            `${c['name']} Weekly Ad ${c['city']} ${c['state']}`,
        expired_flyer_description: (c) =>
            `This weekly ad for ${c['name']} in the city of ${c['city']} in the state of ${c['state']} expired. It has been replaced with a newer ${c['name']} weekly ad`,
        expired_flyer_h1: (c) =>
            `${c['city']} ${c['state']} ${c['name']} weekly ad with id ${c['id']} that expired`,
        expired_flyer_body: (c) =>
            `This weekly ad was for ${c['name']} in the city of ${c['city']} and the state of ${c['state']}. The ${c['name']} weekly ad with id ${c['id']} expired. A new weekly ad for is available and user looking for weekly ad from ${c['name']} in ${c['city']} ${c['state']} are being redirected to the ${c['name']} ${c['city']} ${c['state']} weekly ad. `,

        // Expiread Metadata Item
        expired_item_title: (c) => `${c['name']} ${c['city']} ${c['state']}`,
        expired_item_description: (c) =>
            `This item was ${c['name']} in the city of ${c['city']} in the state of ${c['state']}. The ${c['name']} item expired`,
        expired_item_h1: (c) =>
            `${c['name']} with item id ${c['id']} ${c['city']} ${c['state']}`,
        expired_item_body: (c) =>
            `This item was ${c['name']} in the city of ${c['city']} and the province of ${c['state']}. The ${c['name']} with item id ${c['id']} item expired. Since there is not always a new matching ${c['name']} item from the same retailer in ${c['city']} ${c['state']}, we are redirecting users to the page listing the latest weekly ads and items from ${c['city']} ${c['state']}`,

        // Expiread Metadata Coupon
        expired_coupon_title: (c) =>
            `${c['name']} Coupon in ${c['city']} ${c['state']} with Id ${c['id']}`,
        expired_coupon_description: (c) =>
            `This ${c['name']} coupon with the id ${c['id']} in the city of ${c['city']} and the state of ${c['state']} expired. Users are redirected to relevant content`,
        expired_coupon_h1: (c) =>
            `${c['name']} Coupon id ${c['id']} in ${c['city']} ${c['state']} that expired`,
        expired_coupon_body: (c) =>
            `This ${c['name']} coupon with the id ${c['id']} in the city of ${c['city']} and the state of ${c['state']} expired. Since there is not always a new matching coupon for ${c['name']} ${c['id']} from the same retailer in ${c['city']} ${c['state']}, the user has been redirected to the page listing the latest flyers, items and coupons from ${c['city']} ${c['state']}`,

        // Keywords
        keywords_homepage: (c) =>
            `stores, deals, sales, promotions, offers, flipp, ${c['country']}, weekly ads, circulars`,
        keywords_citypage: (c) =>
            `${c['city']}, stores, deals, sales, promotions, offers, flipp, ${c['country']}, weekly ads, circulars`,
        keywords_flyerpage: (c) =>
            `${c['name']} Flyer, ${c['name']}, ${c['name']} Weekly Flyer, flyers, online flyers, ${c['country']} store flyers, deals, sales, promotions, offers, flipp, ${c['country']}, weekly ads, circulars`,
        keywords_storepage: (c) =>
            `${c['cities']}, stores, deals, sales, promotions, offers, flipp, ${c['country']}, weekly ads, circulars, ${c['merchant']}, ${c['merchant']} deals, ${c['merchant']} flyers, ${c['merchant']} stores, ${c['merchant']} offers, ${c['merchant']} promotions`,
        keywords_flyers_page: (c) =>
            `${c['city']}, stores, deals, sales, promotions, offers, flipp, ${c['country']}, weekly ads, circulars`,
        keywords_flyers_categoriespage: (c) =>
            `${c['category']}, stores, deals, sales, promotions, offers, flipp, country name, weekly ads, circulars`,
        keywords_couponspage: (c) =>
            `${c['name']}, ${c['city']}, coupons, deals, sales, promotions, offers, flipp, weekly ads, circulars, flyers, ${c['country']}`,
        keywords_itempage: (c) =>
            `${c['name']}, ${c['flyerName']} Flyer, ${c['flyerName']}, ${c['flyerName']} Weekly Flyer, flyers, online flyers, ${c['country']} store flyers, deals, sales, promotions, offers, flipp, ${c['country']}, weekly ads, circulars`,
        keywords_coupons: (c) =>
            `${c['city']}, coupons, deals, sales, promotions, offers, flipp, weekly ads, circulars, flyers, ${c['country']}`,
        keywords_blackfriday:
            'black friday sales, best black friday deals, black friday ads, black friday 2021, black friday specials, best black friday tv deals, black friday deals canada, the black friday, black friday ad, black friday shopping, best black friday, black friday online deals, black friday online, best black friday sales, online black friday deals, best air fryer deals, best ipad deals, best apple watch deals, best laptop deals, best nintendo switch deals, cyber monday deals, cyber monday, cyber monday specials',

        // Blackfriday Keywords
        bf_keywords: (c) =>
            `black friday sales, best black friday deals, black friday ads, black friday 2021, black friday specials, best black friday tv deals, black friday deals canada, the black friday, black friday ad, black friday shopping, best black friday, black friday online deals, black friday online, best black friday sales, online black friday deals, best air fryer deals, best ipad deals, best apple watch deals, best laptop deals, best nintendo switch deals, cyber monday deals, cyber monday, cyber monday specials, Black Friday, Black Friday Deals, Black Friday ${c['country']} ${c['year']}, Black Friday ${c['country']}, Black Friday ${c['year']} ${c['country']}, ${c['country']} Black Friday`,
        bf_title: () => ' Black Friday',
        bf_description: () => '. Black Friday on Flipp',
        flyer_title: (c) => `${c['storeName']} Weekly Ad`,
        flyer_location_title: (c) => `${c['city']} Weekly Ads`,
        footer_yourstory: 'Your Story',
        footer_company: 'Company',
        footer_blog: 'Blog',
        footer_retailers: 'Retailers',
        footer_locations: 'Locations',
        footer_careers: 'Careers',
        footer_connect: 'Connect',
        footer_email: 'Email',
        footer_facebook: 'Facebook',
        footer_twitter: 'Twitter',
        footer_instagram: 'Instagram',
        footer_contact: 'Contact',
        footer_copyright: '© Copyright 2023 Flipp Corp. All rights reserved.',
        footer_browseFlyersByCityAndStore:
            'Browse Weekly Ads by City and Store',
        footer_browseFlyersByCity: 'Browse Weekly Ads by City',
        footerAbout: 'About',
        footerForRetailers: 'Retailers',
        footerCareers: 'Careers',
        footerStores: 'Stores',
        footerBlog: 'Blog',
        footerCopyright:
            'Copyright \u00A9 {{year}} Flipp Inc., All rights reserved',
        footerTerms: 'Terms',
        footerPrivacy: 'Privacy',
        footerUSAFlyers: 'Canadian Weekly Ads',
        footerStoresFlyers: 'Store Weekly Ads',
        footerStoresFlyersByCity: 'Store Weekly Ads by City',
        footerCountryCityFlyers: 'USA Cities Weekly Ads',

        // Store page
        store_title: (c) => `${c['storeName']} Weekly Ads`,
        store_location_title: (c) =>
            `${c['storeName']} Weekly Ads in ${c['city']}`,
        store_no_location_title: (c) => `${c['storeName']} Weekly Ads`,
        store_locationStore: (c) => `${c['city']} Stores`,
        store_blackfriday_promo: (c) =>
            `The ${c['storeName']} Black Friday weekly ad is here!`,
        store_currentFlyers: (c) => `Current ${c['storeName']} Weekly Ads`,
        store_upcomingFlyers: (c) => `Upcoming ${c['storeName']} Weekly Ads`,
        store_popularFlyers: 'Popular Weekly Ads',
        store_popularCityFlyers: (c) => `Popular Weekly Ads in ${c['city']}`,
        store_storeCityFlyers: (c) => `${c['store']} store in other cities`,
        store_aboutStore: (c) => `About ${c['store']}`,
        store_aboutFlipp_title: 'About Flipp',
        store_noFlyers: (c) =>
            `Sorry there are currently no weekly ads for this ${c['storeName']} in ${c['city']}`,
        store_noFlyers_noCity: (c) =>
            `Sorry there are currently no weekly ads for this ${c['storeName']}`,
        store_aboutFlipp_text:
            'Browse weekly ads, search for products and sync your shopping list with Flipp.',
        store_seoThumbnail: (c) =>
            `${c['storeName']} Weekly Ad in ${c['city']} which has deals for ${c['category']}`,
        upcoming: 'Upcoming',
        downloadAppBanner_promo:
            'Browse the Latest Weekly Ads on your phone with Flipp!',
        downloadAppBanner_qr: 'Try it for free now',
        openFlyer: 'Open Weekly Ad',
        helpCenter: 'Help Center',
        home: 'Home',

        // Reebee integration
        welcome_to_flipp_title: () => 'Welcome to Flipp!',
        welcome_to_flipp_button: () => 'Got it',
        welcome_to_flipp_text: () =>
            'We are so glad you stopped by. Have a look around and check out our content and features.  We hope you love it.',

        // SDE footer
        powered_by_flipp: 'Powered by Flipp',
        patent: 'Patent',
    },
    'en-ca': {
        download_btn: 'Download Flipp',
        'route:browse': (c) =>
            c['prefix']
                ? `${c['prefix']}/flyer/${c['slug']}`
                : `/flyer/${c['slug']}`,
        'route:listing': (c) =>
            `${c['prefix']}/flyers${c && c['category'] ? `/${c['category']}` : ''}`,

        coupons_description: (c) =>
            `We've matched coupons for your favourite brands with deals in your local ${c['city']} flyers.`,
        // SEO Categoy Flyers
        category_title: (c) =>
            `${c['city']} Weekly ${c['category']} Flyers and Deals | Flipp`,
        category_description: (c) =>
            `Browse the ${c['category']} flyers and circulars in ${c['city']}. Save by discovering products that match your budget and needs`,
        category_flyers_for_city_postal_code_category: (c) =>
            `${c['city']} ${c['category']} flyers`,
        category_flyers_alt: (c) => `${c['flyerName']} in ${c['city']}`,
        top_flyers: () => 'Top Flyers',

        // SEO Coupon Page
        coupon_page_title: (c) => {
            if (c.discount) {
                return `Coupon: Save ${c['discount']} on ${c['store']} in ${c['city']} | Flipp`
            }

            return `Coupon: Save ${c['name']} in ${c['city']} | Flipp`
        },
        coupon_page_description: (c) => {
            if (c.discount) {
                return `Free Printable Coupon: Save ${c['discount']} on ${c['store']} in ${c['city']}`
            }

            return `Free Printable Coupon: Save ${c['name']} in ${c['city']}`
        },
        coupon_page_h1: (c) => `${c['name']} Free Coupon`,

        clippings_page_title: `Your flyer clippings`,
        clippings_page_description: `Your flyer clippings.`,
        index_page_title: ' Flipp – Flyers, Shopping List, Weekly Ads | Flipp',
        index_page_description:
            'Flipp is the easiest way to browse flyers and save money. Quickly browse the brands you love, clip items straight to your shopping list, and highlight top deals across flyers.',
        index_page_h1:
            'Save time and money weekly with digital flyers on Flipp.',

        index_city_title: (c) =>
            `${c['city']} Flyers, Deals & Online Coupons | Flipp`,
        index_city_description: (c) =>
            `View the latest flyers and online coupons from your favourite ${c['city']} stores. Find hand-picked deals, make a shopping list, and save on your weekly shopping!`,
        index_city_h1: (c) => `${c['city']} Flyers, Coupons & Deals`,

        index_flyers_title: (c) =>
            `${c['city']} Weekly Flyers and Deals | Flipp`,
        index_flyers_description: (c) =>
            `Save with ${c['city']} weekly store flyers and deals. Find the best ${c['city']} deals on grocery, electronics, home improvements and more!`,
        index_flyers_h1: (c) => `${c['city']} Weekly Flyers`,

        store_flyers_title: (c) =>
            `${c['store']} Flyers, Coupons, Discounts & Weekly Deals  | Flipp`,
        store_flyers_description: (c) =>
            `Browse deals and special offers in ${c['store']} with Flipp. Flyers and coupons available on website and app today!`,
        store_flyers_h1: (c) => `${c['store']}`,
        store_flyers_h2: (c) => `Top ${c['store']} Flyers`,

        index_city_coupon_title: (c) =>
            `Save with Free Coupons in ${c['city']} | Flipp`,
        index_city_coupon_description: (c) =>
            `Browse free coupons and promo codes for top stores in ${c['city']}, ${c['state']}. Save on groceries, food, retail, health and household products with promo codes, coupons and loyalty cards.`,
        index_city_coupon_h1: (c) => `Save with ${c['city']} coupons`,

        index_item_title: (c) => `Buy ${c['item']} in ${c['city']} | Flipp`,

        index_coupon_category_title: (c) =>
            `Save with Free ${c['category']} Coupons in ${c['city']} | Flipp`,
        index_coupon_category_description: (c) =>
            `Browse free ${c['category']} coupons and promo codes for top stores in ${c['city']}, ${c['state']}. Find the best online ${c['category']} coupons this week to use at your favorite store near you.`,
        index_coupon_category_h1: (c) =>
            `Save with ${c['city']} ${c['category']} coupons`,

        index_single_flyer_title: (c) =>
            `${c['name']} – ${c['city']} Flyers & Circulars | Flipp`,
        index_single_flyer_description: (c) =>
            `Discover products on sale, deals and coupons at ${c['merchant']}, ${c['city']}. ${c['validity']}`,
        index_single_flyer_h1: (c) => `${c['name']} in ${c['city']}`,

        xbrowse_more_flyers: 'More Flyers',
        xbrowse_see_all_flyers: 'See All Flyers',
        my_location_desc:
            'Enter your postal code so we can find the right flyers near you.',
        postal_code_tip: 'There is no content available for this location',

        //
        'All Flyers': 'All Flyers',
        flyers: 'Flyers',
        weekly_ads: 'Weekly Ads',
        flyer: 'Flyer',
        item: 'Items',
        items: 'Items',
        Restaurants: 'Restaurants',

        // Landing Page strings ---------->
        landing_page_description:
            'Flipp makes weekly shopping easy with the most digital flyers and coupons from the brands you love',
        enter_postal_code_error_message: 'Please enter a valid postal code.',
        find_weekly_ads: 'Find your weekly flyers for',
        enter_postal_code:
            "Enter your postal code and we'll find weekly ads in your area",
        browse_description: 'Browse through hundreds of your weekly flyers',
        flyers_found: (c) =>
            `We've found you ${c['num']} local weekly flyers from your favourite retailers`,
        browse_flyer: 'Select a flyer to start browsing',
        more_flyers: (c) => `See ${c['num']} more flyers in ${c['city']}`,
        all_flyers: (c) => `See all flyers in ${c['city']}`,
        mobile_value:
            'Add your loyalty cards, sync your shopping list across devices, and share your list with your shopping partner  — only on the app!',
        landing_page_title: 'Flyers, Coupons and Shopping List',
        deal_search_desktop: 'Try searching for ‘eggs’',
        search_placeholder: 'Search flyers or items',
        search_category_flyers: 'Flyers',
        flyer_items: 'Flyer Items',
        read_weekly_ad: 'Read Flyer',

        flyers_for_postal_code: (c) => `Flyers for ${c['postalCode']}`,
        no_flyers_for_postal_code: (c) =>
            `No flyers for postal code '${c['postalCode']}'.`,
        no_flyers_for_store: (c) => `No flyers for ${c['store']}...`,
        flyers_near_you: () => 'Flyers near you',
        other_flyers_near_you: () => 'Other flyers near you',
        tombstone_msg:
            'Sorry, there are currently no flyers for this retailer.',
        item_disclaimer:
            'In the event of disagreement between the flyer and this popup, the flyer shall take precedence.',
        featured_sort_help: 'Show promoted flyers',
        latest_sort_help: 'Sort flyers by publication date',
        alphabetical_sort_help: 'Sort flyers alphabetically by store',
        next_flyer: 'Next Flyer',
        find_flyers: 'Find Flyers',
        empty_clippings_subtitle:
            'Start browsing flyers to build your clipping list.',
        empty_clippings_button: 'Start Browsing',
        enter_your_postal_code:
            'Enter your postal code so we can find the right flyers near you.',

        // New Landing Page strings ---------->
        flyers_header_category: 'Flyers',
        how_to_save_feature1:
            '{#1}Type your postal code{\\1} below to see the latest deals near you.',
        flyers_feature_heading:
            'Save at over 2,000 of your {#1}favourite stores.{\\1}',
        flyers_feature_description1:
            'Flipp is your one-stop marketplace for savings and deals. Browse weekly digital flyers from retailers near you, or search for the items you need.',
        flyers_feature_description2_part1:
            'For the best mobile experience and to browse products on-the-go, download the ',
        flyers_feature_description2_part2:
            ' today. Available on the App Store, Google Play, and Huawei AppGallery.',
        flipp_app_title: 'Flipp App',
        flyers_feature_cta: 'Browse Flyers',
        sl_feature_description:
            'Easily create a shopping list by adding items from flyers or adding in your own. Find the best deals on your products and easily save your shopping list and share it with a loved one. Print the list or load it on your Flipp app and have it available on the go!',

        // Footer strings ---------->
        webgl_warning_cta:
            'Enable WebGL now to have the best viewing experience possible, or you can continue viewing the flyer as is.',

        // Error Page strings ---------->
        missing_page_cta_msg:
            "While we're cleaning up, please continue browsing our latest flyers.",
        missing_page_cta_button: 'Start Browsing Flyers',

        // Autocomplete strings ----->
        autocomplete_flyer: '1 flyer available',
        autocomplete_flyers: (c) => `${c['num']} flyers available`,

        // Favourites --------->
        fav_page: 'Favourites',
        fav_title: 'Favourites - Flipp',
        fav_h2: 'You currently do not have any favourite stores.',
        fav_desc:
            'Click the [HEART] on your favourite stores to see their flyers in your favourites.',

        // Breadcrumbs
        br_home: 'Canada',

        // Seo Search Page Metadata
        search_page_title: (c) =>
            `Find the Best Deals for ${c['q']} in ${c['city']}, ${c['state']} | Flipp`,
        search_page_description: (c) =>
            `Save on ${c['q']} in ${c['city']} with Flipp deals and coupons. Explore all ads and discounts about ${c['q']}, browse flyers and deals in ${c['city']} and enjoy great savings.`,
        search_page_h1: (c) =>
            `Deals and Coupons for ${c['q']} in ${c['city']}`,
        search_page_h2: (c) => `${c['q']} - ${c['city']} - ${c['itemType']}`,
        search_page_h3: (c) => `${c['itemName']} - ${c['status']}`,
        search_page_alt: (c) => `${c['itemName']} Deal in ${c['city']}`,

        // Expiread Metadata Flyer
        expired_flyer_title: (c) =>
            `${c['name']} Flyer ${c['city']} ${c['state']}`,
        expired_flyer_description: (c) =>
            `This flyer for ${c['name']} in the city of ${c['city']} in the province of ${c['state']} expired. It has been replaced with a newer ${c['name']} flyer`,
        expired_flyer_h1: (c) =>
            `${c['city']} ${c['state']} ${c['name']} flyer with id ${c['id']} that expired`,
        expired_flyer_body: (c) =>
            `This flyer was for ${c['name']} in the city of ${c['city']} and the state of ${c['state']}. The ${c['name']} flyer expired. A new flyer for is available and  user looking for flyers from ${c['name']} in ${c['city']} ${c['state']} are being redirected to the ${c['name']}${c['city']}${c['state']} flyer.`,

        // Expiread Metadata Item
        expired_item_title: (c) => `${c['name']} ${c['city']} ${c['state']}`,
        expired_item_description: (c) =>
            `This flyer item was a ${c['name']} in the city of ${c['city']} in the province of ${c['state']}. The flyer ${c['name']} item expired and user are redirected.`,
        expired_item_h1: (c) =>
            `${c['name']} with item id ${c['id']} ${c['city']} ${c['state']}`,
        expired_item_body: (c) =>
            `This item was ${c['name']} with item id ${c['id']} in the city of ${c['city']} and the province of ${c['state']}. The ${c['name']} item expired. Since there is not always a new matching ${c['name']} item from the same retailer in ${c['city']} ${c['state']}, we are redirecting users to the page listing the latest flyers and items from ${c['city']} ${c['state']}`,

        // Expiread Metadata Coupon
        expired_coupon_title: (c) =>
            `${c['name']} Coupon in ${c['city']} ${c['state']} with Id ${c['id']}`,
        expired_coupon_description: (c) =>
            `This ${c['name']} coupon with the id ${c['id']} in the city of ${c['city']} and the province of ${c['state']}. Users are redirected to relevant content`,
        expired_coupon_h1: (c) =>
            `${c['name']} Coupon id ${c['id']} in ${c['city']} ${c['state']} that expired`,
        expired_coupon_body: (c) =>
            `This ${c['name']} coupon with the id ${c['id']} in the city of ${c['city']} and the province of ${c['state']} expired. Since there is not always a new matching coupon for ${c['name']} ${c['id']} from the same retailer in ${c['city']} ${c['state']}, the user has been redirected to the page listing the latest flyers, items and coupons from ${c['city']} ${c['state']}`,

        // Mobile Bananer
        mobile_popup_banner:
            'View this flyer in the Flipp app for the best reading experience.',

        // Black Friday Page
        view_more: 'More Tips',
        blackfriday_blog_title: 'Blogs',
        blackfriday_title: () => `Black Friday Flyers and Deals | Flipp`,
        blackfriday_description: () =>
            `Save with Black Friday Flyers and Deals. Find the best Black Friday deals on grocery, electronics, home improvements and TV!`,
        blackfriday_deals: 'See Black Friday Deals',
        cyberweek_deals: 'See Cyber Week Deals',
        blackfriday_banner: 'Black Friday Deals and Coupons on Flipp',
        blackfriday_banner_title1: 'Snag your ultimate',
        blackfriday_banner_title2: 'Black Friday deals',
        cyberweek_banner_title: 'Flipp: Cyber Week Deals are Here!',
        blackfriday_banner_subtitle:
            'Find the hottest deals from your favorite stores',
        blackfriday_home_banner: 'Black Friday Deals and Coupons on Flipp',
        blackfriday_home_wording: 'The New Black Friday is Now!',
        no_flyers_for_blackfriday_page: 'Black Friday Flyers are coming soon.',
        giveaway_title: 'Black Friday Giveaway',
        giveaway_banner_title: 'Black Friday Giveaway',
        giveaway_banner_subtitle: 'Daily prizes to be won!',
        giveaway_banner_date: 'November 18 – December 6, 2024',
        giveaway_banner_button_text: 'Enter Today’s Giveaway',
        giveaway_christmas_title: 'Days of Christmas Giveaway',
        giveaway_christmas_subtitle: 'Daily prizes worth $500',
        giveaway_christmas_button_text: 'Enter now',

        giveaway_back_to_school_title1: 'Back-to-School',
        giveaway_back_to_school_title2: 'Giveaway',
        giveaway_back_to_school_date: 'August 21–24, 2024',
        giveaway_back_to_school_description:
            'Up to $2,000 CAD in prizes to be won!',

        // Proudly Canadian deals
        proudly_canadian_banner_title:
            'Save at Your Favourite Canadian-Owned Stores',
        proudly_canadian_banner_title1: 'Save at Your Favourite',
        proudly_canadian_banner_title2: 'Canadian-Owned Stores',
        proudly_canadian_deals: 'Find Deals Now',
        proudly_canadian_disclaimer:
            "We aim to feature Canadian-owned businesses or products made in Canada based on trusted third-party data. However, we haven't independently verified ownership and can't guarantee accuracy. We encourage conducting your own research.",

        flyersCountry_title: 'Canadian Flyer Collection',
        storeCityFlyers_title: 'Browse Store Flyers by City',
        flyersCountry_content1:
            'With our collection of Canadian flyers, Flipp brings you all the latest info on special promotions, sales, discounts and deals from retailers across Canada.',
        flyersCountry_content2:
            'Helping you shop smarter, Flipp is always up to date with the latest flyer information. Every time new flyers go into circulation, Flipp helps you find the best ways to save money on virtually any purchase. Whether you are shopping for clothing, furniture, groceries, toys, food, school supplies, or home decor, Flipp makes it easy to find what you need at the best price.',
        flyersCountry_content3:
            'Our collection of weekly flyers from top Canadian stores, including Canadian Tire, Princess Auto, Sport Chek, Brault & Martineau, Toys “R” Us, Giant Tiger and more, brings the bargains to you! All the weekly ads are available on our website, but if you want to have the discounts at your fingertips, download the Flipp app today.',
        flyersCountry_content4_a: 'Available for ',
        flyersCountry_content4_or: 'or',
        flyersCountry_content4_b:
            ' devices, the Flipp app is one of the most powerful savings tools you can find. Bringing together everything from electronics flyers to grocery coupons, the Flipp app lets you quickly find and compare prices from store to store, stay organized with a single shopping list, and when you sign in, your account automatically syncs across devices, so you never have to worry about losing track of the biggest savings.',
        flyersCountry_content5:
            'No matter where you are in Canada, flyers and coupons are a great way to keep more cash in your pocket. Leaving no postal code unserved, our collection of Canadian flyers includes businesses from Canadian cities of all sizes. ',
        flyersCountry_top10: 'Top 10 Cities - Local Flyers',
        flyersCountry_footnote:
            'Note: All logos and trademarks are property of their respective owners',
        flyersCountry_all: 'Browse Flyers From Across Canada',
        flyer_title: (c) => `${c['storeName']} Flyer`,
        flyer_location_title: (c) => `${c['city']} Flyers`,
        footer_yourstory: 'Your Story',
        footer_company: 'Company',
        footer_blog: 'Blog',
        footer_retailers: 'Retailers',
        footer_locations: 'Locations',
        footer_careers: 'Careers',
        footer_connect: 'Connect',
        footer_email: 'Email',
        footer_facebook: 'Facebook',
        footer_twitter: 'Twitter',
        footer_instagram: 'Instagram',
        footer_contact: 'Contact',
        footer_copyright: '© Copyright 2023 Flipp Corp. All rights reserved.',
        footer_browseFlyersByCityAndStore: 'Browse Flyers by City and Store',
        footer_browseFlyersByCity: 'Browse Flyers by City',
        footerAbout: 'About',
        footerForRetailers: 'Retailers',
        footerCareers: 'Careers',
        footerStores: 'Stores',
        footerBlog: 'Blog',
        footerCopyright:
            'Copyright \u00A9 {{year}} Flipp Inc., All rights reserved',
        footerTerms: 'Terms',
        footerPrivacy: 'Privacy',
        footerCanadianFlyers: 'Canadian Flyers',
        footerStoresFlyers: 'Store Flyers',
        footerStoresFlyersByCity: 'Store Flyers by City',
        footerCountryCityFlyers: 'Canadian City Flyers',

        //Coupon
        see_in_weekly_ad: 'See it in the flyer',

        // Store page
        store_title: (c) => `${c['storeName']} Flyers`,
        store_location_title: (c) => `${c['storeName']} Flyers in ${c['city']}`,
        store_no_location_title: (c) => `${c['storeName']} Flyers`,
        store_locationStore: (c) => `${c['city']} Stores`,
        store_blackfriday_promo: (c) =>
            `The ${c['storeName']} Black Friday flyer is here!`,
        store_currentFlyers: (c) => `Current ${c['storeName']} Flyers`,
        store_upcomingFlyers: (c) => `Upcoming ${c['storeName']} Flyers`,
        store_popularFlyers: 'Popular Flyers',
        store_popularCityFlyers: (c) => `Popular Flyers in ${c['city']}`,
        store_storeCityFlyers: (c) => `${c['store']} store in other cities`,
        store_aboutStore: (c) => `About ${c['store']}`,
        store_aboutFlipp_title: 'About Flipp',
        store_noFlyers: (c) =>
            `Sorry there are currently no flyers for this ${c['storeName']} in ${c['city']}`,
        store_noFlyers_noCity: (c) =>
            `Sorry there are currently no flyers for this ${c['storeName']}`,
        store_aboutFlipp_text:
            'Browse flyers, search for products and sync your shopping list with Flipp.',
        store_seoThumbnail: (c) =>
            `${c['storeName']} Flyer in ${c['city']} which has deals for ${c['category']}`,
        upcoming: 'Upcoming',
        downloadAppBanner_promo:
            'Browse the Latest Flyers on your phone with Flipp!',
        downloadAppBanner_qr: 'Try it for free now',
        openFlyer: 'Open Flyer',
        helpCenter: 'Help Center',
        home: 'Home',

        // Reebee integration
        welcome_to_flipp_title: () => 'Welcome to Flipp!',
        welcome_to_flipp_button: () => 'Got it',
        welcome_to_flipp_text: () =>
            'We are so glad you stopped by. Have a look around and check out our content and features.  We hope you love it.',
        'help-reebee': () =>
            'https://help.flipp.com/whats-happening-with-reebee-com',
    },
    fr: {
        'route:browse': (c) =>
            c['prefix']
                ? `${c['prefix']}/circulaire/${c['slug']}`
                : `/circulaire/${c['slug']}`,
        'route:item': (c) => `${c['prefix']}/produit/${c['slug']}`,
        'route:coupon': (c) => `${c['prefix']}/coupon/${c['slug']}`,
        'route:coupons': (c) =>
            `${c['prefix']}/coupons${c && c['category'] ? `/${c['category']}` : ''}`,
        'route:listing': (c) =>
            `${c['prefix']}/circulaires${c && c['category'] ? `/${c['category']}` : ''}`,
        'route:shopping_list': '/liste_dachats',
        'route:clippings': '/coupures',
        'route:install': '/telechargez',
        'route:search': (c) => `/recherche/${c['q']}`,
        'route:redirect': '/redirect',
        'route:favourites': '/favoris',
        'route:home': '/accueil',
        'route:distribution': '/dist',
        'route:playstore': '/playstore',
        'route:appstore': '/appstore',

        // CDPv3
        cdpv3_back: (c) => `Retourner à ${c['site']}`,
        cdpv3_powered_by: 'Conçu par',
        download_btn: 'Téléchargez Flipp',
        coupons_title: 'Coupons',
        coupons_description: (c) =>
            `Nous avons jumelés des coupons sur vos marques favoris avec les aubaines dans les circulaires locales à ${c['city']}.`,
        // SEO Categoy Flyers
        category_title: (c) =>
            `${c['category']} : les circulaires en ligne de ${c['city']} | Flipp`,
        category_description: (c) =>
            `Consultez les circulaires et spéciaux actuels dans la catégorie ${c['category']} à ${c['city']}.`,
        category_flyers_for_city_postal_code_category: (c) =>
            `Circulaires - ${c['category']} - ${c['city']}`,
        category_flyers_alt: (c) => `${c['flyerName']} à ${c['city']}`,
        top_flyers: () => 'Meilleures circulaires hebdomadaires',

        // SEO Coupon Page
        coupon_page_title: (c) => {
            if (c.discount) {
                return `Coupon: Économiser ${c['discount']} avec ${c['store']} à ${c['city']} | Flipp`
            }

            return `Coupon: Économiser ${c['name']} à ${c['city']} | Flipp`
        },
        coupon_page_description: (c) => {
            if (c.discount) {
                return `Coupons gratuits: Économisez ${c['discount']} sur ${c['store']} à ${c['city']}. Trouvez tous les echantillons gratuits, coupons rabais à imprimer et coupon en ligne sur Flipp.`
            }

            return `Coupons gratuits: Économisez ${c['name']} à ${c['city']}. Trouvez tous les echantillons gratuits, coupons rabais à imprimer et coupon en ligne sur Flipp.`
        },
        coupon_page_h1: (c) => `Coupon pour ${c['name']}`,

        item_details_title: (c) => `${c['name']}`,
        item_details_description: (c) => `${c['name']} - ${c['description']}`,

        clippings_page_title: `Vos articles`,
        clippings_page_description: `Vos articles.`,

        // Seo Search Page Metadata
        search_page_title: (c) =>
            `Trouvez des rabais sur ${c['q']} à ${c['city']}, ${c['state']} | Flipp`,
        search_page_description: (c) =>
            `${c['q']} à ${c['city']}. Économisez avec Flipp! Découvrez tous les rabais, coupons gratuits et offres promotionnelles pour : ${c['q']}.`,
        search_page_h1: (c) => `Rabais et Coupons pour ${c['q']} à {c["city"]}`,
        search_page_h2: (c) => `${c['q']} - ${c['city']} - ${c['itemType']}`,
        search_page_h3: (c) => `${c['itemName']} - ${c['status']}`,
        search_page_alt: (c) => `Résultat pour ${c['itemName']} à ${c['city']}`,

        index_page_title: 'Circulaires et coupons rabais en ligne | Flipp',
        index_page_description:
            'Consultez les dernières circulaires hebdomadaires et les aubaines de vos magasins préférés. Trouvez des offres et des coupons pour économiser partout au Québec!',
        index_page_h1:
            'Économisez temps et argent chaque semaine grâce aux circulaires numériques sur Flipp.',

        index_city_title: (c) =>
            `Circulaires et coupons en ligne à ${c['city']} | Flipp`,
        index_city_description: (c) =>
            `Consultez les dernières circulaires hebdomadaires et les aubaines de vos magasins préférés à ${c['city']}. Trouvez des offres et des coupons pour économiser partout au Québec!`,
        index_city_h1: (c) => `Circulaires et coupons en ligne à ${c['city']}`,

        store_flyers_title: (c) =>
            `${c['store']} Circulaires, coupons, rabais et offres hebdomadaires | Flipp `,
        store_flyers_description: (c) =>
            `Parcourez les offres et offres spéciales dans ${c['store']} avec Flipp. Circulaires et coupons disponibles sur le site Web et l'application aujourd'hui!`,
        store_flyers_h1: (c) => `${c['store']}`,
        store_flyers_h2: (c) => `Meilleurs ${c['store']} Circulaires`,

        index_flyers_title: (c) =>
            `Les circulaires en ligne de ${c['city']} | Flipp`,
        index_flyers_description: (c) =>
            `Consultez les circulaires et spéciaux actuels de tous vos commerces préférés à ${c['city']}.`,
        index_flyers_h1: (c) => `Les circulaires en ligne de ${c['city']}`,

        index_city_coupon_title: (c) =>
            `Économisez avec des coupons gratuits à ${c['city']} | Flipp`,
        index_city_coupon_description: (c) =>
            `Trouvez tous les coupons gratuits à ${c['city']} de vos magasins et restaurants préférés. Économisez sur une foule d'activités et vos produits favoris.`,
        index_city_coupon_h1: (c) =>
            `Économisez avec les coupons à ${c['city']} | Flipp`,

        index_coupon_category_title: (c) =>
            `${c['category']} : coupons gratuits à ${c['city']} | Flipp`,
        index_coupon_category_description: (c) =>
            `Trouvez tous les coupons gratuits à ${c['city']}  de la categorie ${c['category']}. Économiser sur tous vos produits favoris.`,
        index_coupon_category_h1: (c) =>
            `${c['category']} Économisez à ${c['city']}`,

        index_item_title: (c) =>
            `${c['item']} à $${c['price']} chez ${c['merchant']} | Flipp`,

        index_single_flyer_title: (c) => `${c['name']} - ${c['city']} | Flipp`,
        index_single_flyer_description: (c) =>
            `Découvrez les meilleures offres chez ${c['merchant']}, ${c['city']} avec ${c['validity']}`,
        index_single_flyer_h1: (c) => `${c['name']} à ${c['city']}`,
        //
        delete_all: 'Supprimer la liste',
        delete_expired: 'Effacer Expiré',
        edit: 'Modifier',
        done: 'Ok',
        expired: 'Expiré',
        and: 'et',
        submit: 'Soumettre',

        empty_clippings_title: "Vous n'avez pas d’articles",
        empty_clippings_subtitle:
            "Commencez à consulter les circulaires pour créer votre liste d'achats.",
        empty_clippings_body:
            'Double-cliquez les articles pour les mettres dans votre liste d’achats.',
        empty_clippings_button: 'Parcourir',
        map: 'Carte',
        clippings: 'Articles',
        add_clip_button: 'Ajouter à la liste',
        remove_clip_button: 'Retirer de la liste',
        download_app_banner_title:
            'Utilisez Flipp sur votre téléphone! Essayez l’application gratuite dès maintenant.',
        store_info: 'Info Magasin',
        stores_for_city: (c) => `Magasins à ${c['city']}`,
        stores: () => 'Magasins',
        stores_closest_to_postal_code: (c) =>
            `Magasins près de ${c['postalCode']}`,
        contest: 'Concours',
        featured_sort_help: 'Montrer les circulaires speciaux',
        latest_sort_help: 'Trier date de publication',
        alphabetical_sort_help: 'Trier par le nom du magasin',
        back_button: 'Retour',
        back_to_sl: "Retour à la liste d'achats",
        close_dialog_button: 'Fermer dialogue',
        open_navigation_menu: 'Ouvrir la menu de navigation',
        results_for_q: (c) => `Resultats pour '${c['q']}'`,
        results_for: 'Resultats pour ',
        enter_your_postal_code:
            'Entrez votre code postal afin de trouver des circulaires.',
        x_dollars_n_cents: (c) => `${c['x']} dollars et ${c['n']} cents`,
        find_flyers: 'Recherche',
        x_of_n_stars: (c) => `${c['x']} de ${c['n']} étoiles`,
        flyers_for_postal_code: (c) => `Circulaires pour ${c['postalCode']}`,
        flyers_for_postal_code_category: (c) =>
            `${c['category']} pour ${c['postalCode']}`,
        flyers_for_city_postal_code_category: (c) =>
            `${c['category']} pour ${c['city']} (${c['postalCode']})`,
        coupons_for_postal_code: (c) => `Coupons pour ${c['postalCode']}`,
        change_location: 'Changer',
        about_us: 'À propos de nous',
        about_us_link: 'https://corp.flipp.com/a-propos-de-flipp/',
        keep: 'Garder',
        delete_account_confirmation:
            'Votre compte sera bientôt supprimé. Nous vous enverrons un email de confirmation sous peu!',
        not_sell_data_confirmation:
            'Flipp ne vendra ni ne partagera vos données personnelles. Gérez vos données à tout moment.',
        ad_blocker_detected:
            "Oups! Il semble qu'un bloqueur de publicités affecte votre expérience sur Flipp. Veuillez le désactiver pour continuer.",
        manage: 'Gérer',
        item_added: 'Article ajouté',
        item_removed: 'Article retiré',
        items_added: 'articles ajoutés',
        to_your_shopping_list: 'à votre liste d’achats.',
        from_your_shopping_list: 'de votre liste d`achats.',
        view: 'Voir',
        delete_account_link: 'Supprimer mon compte',
        delete_account_button: 'Supprimer le compte',
        delete_account_redirect: 'Connectez-vous pour continuer',
        delete_account_title:
            'Êtes-vous sûr de vouloir supprimer votre compte?',
        delete_account_text1:
            'Cela supprimera définitivement le compte suivant:',
        delete_account_text2:
            'En confirmant, nous supprimerons définitivement, regrouperons ou anonymiserons les informations personnelles que vous nous avez fournies ou que nous avons collectées à votre sujet, en fonction des identifiants que vous nous avez donnés.',
        careers: 'Carrières',
        mobile_app: 'Application mobile',
        valid_from_to: (c) => `Valide du ${c['from']} au ${c['to']}`,
        flexible_validity: (c) =>
            `Valide ${c['from'] ? 'du ' + c['from'] : ''} ${c['to'] ? ' au ' + c['to'] : ''}`,
        flyers: 'Circulaires',
        items: 'Produits',
        weekly_ads: 'Circulaires hebdomadaires',
        flyer: 'Circulaire',
        item: 'Produits',
        online: 'En ligne',
        online_items: 'Articles en ligne',
        see_all_online_results: 'Consultez tous les résultats en ligne',
        flyer_items: 'Articles de la circulaire',
        buy_online: 'Acheter en ligne',
        at_merchant_name: (c) => `de ${c['merchantName']}`,
        no_flyers_for_postal_code: (c) =>
            `Aucune circulaire pour ${c['postalCode']}.`,
        no_flyers_for_store: (c) => `Aucune circulaire pour ${c['store']}...`,
        flyers_near_you: () => 'Circulaires à proximité',
        other_flyers_near_you: () => 'Autres circulaires à proximité',
        tombstone_msg:
            "Désolé, il n'y a aucune circulaire disponible pour ce détaillant en ce moment.",
        featured: 'En vedette',
        alphabetical: 'A-Z',
        latest: 'Récentes',
        'All Flyers': 'Tout',
        Groceries: 'Épicerie',
        'Home & Garden': 'Maison et jardin',
        Electronics: 'Électronique',
        'Baby & Kids': 'Enfants et bébés',
        Fashion: 'Mode',
        Automotive: 'Automobile',
        'Sporting Goods': 'Sports',
        Pets: 'Animaux',
        Office: 'Bureau',
        Pharmacy: 'Pharmacie',
        'Black Friday': 'Vendredi fou',
        'Proudly Canadian': 'Fièrement Canadien',
        'Back to School': 'La rentrée scolaire',
        'Boxing Day': 'l’Après-Noël',
        'Boxing Week': 'Lendemain de Noël',
        'General Merchandise': 'Marchandises générales',
        Specialty: 'Spécialité',
        'Online Only': 'Uniquement en ligne',
        Halloween: 'Halloween',
        Thanksgiving: 'Action de grâce',
        'Cyber Monday': 'Cyber lundi',
        'Cyber Week': 'Semaine Cyber',
        Holidays: 'Temps des Fêtes',
        'Holiday Gifting': 'Temps des Fêtes',
        Christmas: 'Noël',
        "New Year's Eve": "Veille du jour de l'An",
        'New Years': 'Jour de l’An',
        "Valentine's Day": 'Saint-Valentin',
        "President's Day": 'Jour des présidents',
        Easter: 'Pâques',
        "Mother's Day": 'Fêtes des Mères',
        "Father's Day": 'Fêtes des Pères',
        Restaurants: 'Restaurants',

        'All Deals': 'Tout',
        Baby: 'Bébé',
        Grocery: 'Épicerie',
        'Health & Beauty': 'Santé et beauté',
        Household: 'Maison',
        Other: 'Autre',

        previous_page: 'Page Precedente',
        next_page: 'Prochaine Page',
        next_flyer: 'Prochaine circulaire',
        zoom_in: 'Zoom avant',
        zoom_out: 'Zoom arrière',
        correction_notice_button: 'Notifications de Corrections',
        correction_notice: 'Notifications de Corrections',
        no_correction_notices:
            "Il n'y a pas de corrections pour ce circulaire.",
        starts_on_date: (c) => `Commence ${c['date']}`,
        starts_on_day: (c) => `Commence ${c['day']}`,
        starts_tomorrow: 'Commence demain',
        starts_today: "Commence aujourd'hui",
        n_years_left: (c) => `${c['n']} années restantes`,
        '1_year_left': 'Une année restante',
        n_months_left: (c) => `${c['n']} mois restants`,
        '1_month_left': 'Un mois restant',
        n_weeks_left: (c) => `${c['n']} semaines restantes`,
        '1_week_left': 'Une semaine restante',
        until_day: (c) => `Jusqu'à ${c['day']}`,
        until_tomorrow: "Jusqu'à demain",
        ends_today: "Se termine aujourd'hui",
        ended_on_date: (c) => `Termine ${c['date']}`,
        no_search_results: (c) => `Aucun resultats pour '${c['q']}'.`,
        search: 'Recherche',
        search_placeholder: 'Recherchez des circulaires ou des articles',
        search_placeholder_mobile: 'Rechercher',
        search_category_all: 'Tout',
        search_category_flyers: 'Circulaires',
        search_category_items: 'Produits',
        search_category_coupons: 'Coupons',
        search_category_online: 'En ligne',
        postal_code: 'Code postal',
        legal: 'Légal',
        company: 'Entreprise',
        accept_terms: `J'ai lu et j'accepte les `,
        accept_privacy: `J'ai lu et j'accepte la `,
        terms_url: 'https://corp.flipp.com/legal-conditions-dutilisation',
        privacy_url:
            'https://corp.flipp.com/legal-politique-de-confidentialite',
        terms_of_use: "Conditions d'utilisation",
        privacy_policy: 'Politique de confidentialité',
        or: 'ou',

        my_data: 'Demandez vos données',
        my_data_title: 'Demander vos données',
        my_data_select_country: 'Sélectionnez votre pays',
        my_data_select_state: 'Choisissez votre état ou province',
        my_data_select_option: 'Choisir une option',
        my_data_resident: 'Je suis résident(e) de:',
        my_data_name: 'Prénom (obligatoire)',
        my_data_surname: 'Nom de famille (obligatoire)',
        my_data_email: 'Courriel (obligatoire)',
        my_data_name_error: 'Veuillez entrer votre prénom',
        my_data_surname_error: 'Veuillez entrer votre nom de famille',
        my_data_email_error: 'Veuillez entrer votre email',
        my_data_california: 'Californie, É-U',
        my_data_colorado: 'Colorado, É-U',
        my_data_connecticut: 'Connecticut, É-U',
        my_data_virginia: 'Virginie, É-U',
        my_data_quebec: 'Québec, CA',
        my_data_select_request:
            'Sélectionnez votre demande de données ci-dessous',
        my_data_select_option_label_do_not_sell: 'Ne pas vendre mes données',
        my_data_select_option_label_receive_copy:
            'Recevoir une copie de mes données',
        my_data_select_option_label_delete_data:
            'Supprimer définitivement mes données',
        my_data_select_option_text_do_not_sell:
            'Nous ne vendrons pas les informations personnelles que vous nous avez fournies ou que nous avons collectées à votre sujet à des tiers.',
        my_data_select_option_text_receive_copy:
            'Nous vous enverrons une copie des informations personnelles que vous nous avez fournies ou que nous avons collectées à votre sujet, en fonction des identifiants que vous nous avez fournis.',
        my_data_select_option_text_delete_data:
            'Nous supprimerons, agrégerons ou dépersonnaliserons les informations personnelles que vous nous avez fournies ou que nous avons collectées à votre sujet, en fonction des identifiants que vous nous avez fournis',
        my_data_text:
            "Vous avez des droits spécifiques concernant vos données en conformité avec la loi applicable. Veuillez remplir les champs ci-dessous et soumettre si vous souhaitez exercer l'un de ces droits. Pour plus d'informations, veuillez consulter notre politique de confidentialité.",
        my_data_disclaimer_text1:
            "Si vous avez des questions ou des préoccupations concernant ce formulaire ou vos droits, veuillez consulter notre politique de confidentialité disponible à l'adresse ",
        my_data_disclaimer_text2:
            "Nous sommes tenus de vérifier votre identité avant de répondre à toute demande d'information ou de suppression, et nous le ferons en vérifiant que les informations personnelles que vous fournissez dans ce formulaire correspondent aux informations contenues dans nos systèmes.",
        my_data_disclaimer_text3:
            "Si vous souhaitez connaître les catégories d'informations collectées par Flipp concernant tous les utilisateurs, veuillez vous référer à la politique de confidentialité.",
        my_data_delete_modal_title:
            'Supprimer vos données Flipp de façon permanente?',
        my_data_delete_modal_confirm: 'Supprimer les données',
        my_data_confirm_success_header: 'Demande envoyée',
        my_data_confirm_success_text1: 'Votre demande a bien été envoyée.',
        my_data_confirm_success_text2:
            'Vous recevrez un courriel de notre part sous peu.',
        my_data_error_modal_title: 'Erreur',
        my_data_error_modal_text: 'Connectez-vous pour soumettre',
        my_data_delete_modal_text:
            'Veuillez noter que ce processus est irréversible et que toute personnalisation de votre expérience sera perdue définitivement.',
        my_data_server_error_message:
            "Une erreur interne s'est produite. Notre équipe travaille à la résoudre. Veuillez réessayer plus tard.",
        my_data_nonetwork_error_message:
            'Un problème est survenu. Vérifiez votre connexion Internet et réessayez.',

        patents: 'Brevets',
        reviews: 'Commentaires',
        overall_rating: 'Cote Globale',
        based_on_x_reviews: (c) => `Basé sur ${c['x']} commentaires`,
        review_by_author: (c) => `Commentaire par ${c['author']}`,
        x_positive_reviews: (c) => `${c['x']} commentaires positives`,
        x_negative_reviews: (c) => `${c['x']} commentaires négatives`,
        x_from_y_price: (c) => `${c['x']} de ${c['y']} pour ${c['price']}`,
        was: 'Était ',
        in_store_only: 'Au magasin seulement',
        description: 'Description',
        features: 'Caractéristiques',
        specs: 'Spécifications',
        item_disclaimer:
            'En cas de différence entre la circulaire et cette fenêtre surgissante, la circulaire a préséance.',
        ecom_disclaimer:
            'Les prix sont fournis par les marchands, et sont sujets à des mises à jour, car les prix peuvent ne pas être à jour. En cas de divergences, le site Internet du marchand aura préséance.',
        leaving_flipp: 'Quitter Flipp',
        click_to_visit_m: (c) => `Cliquez pour visiter ${c['m']}.`,
        matching_coupons: 'Coupons Correspondants',
        save: 'Économisez',
        print: 'Imprimer',
        print_coupon: 'Imprimer le coupon',
        print_list: 'Imprimez la Liste',
        mobile_install_headline: "Téléchargez l'application Flipp et",
        mobile_install_subline:
            'économisez davantage sur vos marques préférées',
        mobile_install_coupons:
            'Naviguez parmi des milliers de coupons, imprimez-les et maximiser vos économies hebdomadaires.',
        mobile_install_clippings:
            'Ajoutez des articles à votre liste avec un seul appui pour mieux planifier vos achats',
        xbrowse_more_flyers: 'Plus de circulaires',
        xbrowse_see_all_flyers: 'Afficher toutes les circulaires',
        page_thumb: (c) => `Page ${c['start']} de ${c['end']}`,
        my_location_title: 'Mon emplacement',
        postal_code_tip: 'Aucun contenu n’est disponible pour cet emplacement',
        my_location_desc:
            'Saisissez votre code postal pour nous permettre de vous proposer les circulaires de magasins près de chez vous.',
        coupon_unavailable_cta: 'Désolé, ce coupon n’est plus disponible',
        coupon_unavailable_limit_message:
            "Ce coupon a atteint la limite maximale d'échanges. Veuillez continuer à naviguer pour d'autres coupons",
        coupon_unavailable_fsa_message:
            "Ce coupon n'est pas disponible dans votre région. Veuillez continuer à naviguer pour d'autres coupon.",
        you_save: 'Vous économisez',
        percent_off: (c) => `Rabais de ${c['percent']}%`,
        feedback_text:
            '“Facile à utiliser et trouver ce que je veux rapidement.”',
        feedback_text_1:
            '“C’est une excellente application qui évite de s’encombrer avec tout un tas de circulaire.”',
        feedback_text_2:
            '“Merci de remplacer, en 100 fois mieux, le vieux publi-sac désuet!”',
        feedback_text_3:
            '“Super quand on cherche quelque chose de spécifique et pas le temps de regarder dans les circulaires!”',
        feedback_text_4:
            '“Merci car avec Flipp la recherche de spéciaux est simplifiée.”',
        feedback_text_5: '“Je ne vais jamais au magasin sans la consulter.”',
        feedback_text_6: "“C'est devenu un outil très essentiel pour moi.”",
        feedback_text_7:
            '“On retrouve tout ce que nous avons besoin en un seul endroit, c’est parfait!”',
        feedback_text_8:
            '“J’adore cette application, facile d’utilisation et très pratique!”',
        feedback_text_9: '“J’adore ca donne de bonnes references!”',
        mobile_banner:
            'Trouvez les meilleures offres sur tout ce que vous recherchez. Essayez la Liste de suivi sur Flipp.',
        mobile_banner_blackfriday:
            "Soyez à l'affût du Vendredi fou avec la Liste de suivi. Seulement avec l'appli Flipp.",

        // Desktop Banner
        desktop_banner_title:
            'Voici la liste de suivi! Seulement sur l’application Flipp.',
        desktop_banner_body:
            'Créez une liste d’offres pour les suivre facilement, et recevez des alertes lorsqu’un article est en promotion. Saisissez votre numéro de téléphone ou numérisez le code QR avec votre appareil pour recevoir un lien de téléchargement.',
        desktop_banner_submit: 'M’envoyer l’application par texto',
        desktop_banner_footer:
            'En saisissant votre numéro de téléphone, vous consentez à ce que nous l’utilisions pour vous envoyer un lien vers l’application et que nous l’utilisions conformément à ',
        desktop_banner_footer_2:
            ' de l’application. Les frais de messagerie standards s’appliquent. Flipp ne partagera pas votre numéro de téléphone avec tout tiers.',
        desktop_banner_privacy_policy: 'la politique de confidentialité',
        desktop_banner_placeholder: 'Saisir le numéro de téléphone',
        'desktop-banner_error':
            'Numéro non valide. Veuillez vérifier le numéro et réessayer.',
        'desktop-banner_success_title': 'Message envoyé!',
        'desktop-banner_success_body': (c) =>
            `Nous venons d’envoyer un message texte contenant le lien de téléchargement au +1 ${c.number}. Veuillez vérifier vos messages et suivre les instructions pour télécharger l’application.`,

        // Black Friday Page
        view_more: 'Obtenir plus de trucs',
        blackfriday_blog_title: 'Blogs',
        blackfriday_title: () =>
            `Circulaires et offres du Vendredi fou | Flipp`,
        blackfriday_description: () =>
            `Économisez avec les circulaires et les offres du Vendredi fou. Trouvez les meilleures offres du Vendredi fou sur l'épicerie, l'électronique, les rénovations et les téléviseurs!`,
        blackfriday_deals: 'Voir les offres du Vendredi fou',
        cyberweek_deals: 'Voir les soldes de la Cybersemaine',
        blackfriday_banner_title1: 'Saisissez vos offres',
        blackfriday_banner_title2: 'exceptionnelles du',
        blackfriday_banner_title3: 'Vendredi Fou',
        cyberweek_banner_title:
            'Flipp : C’est le temps des soldes de la Cybersemaine!',
        blackfriday_banner_subtitle:
            'Trouvez les offres les plus alléchantes de vos magasins préférés.',
        blackfriday_home_wording: 'Le Vendredi fou nouveau genre est arrivé!',
        no_flyers_for_blackfriday_page:
            'Les circulaires du Vendredi fou arrivent bientôt.',
        giveaway_title: 'Concours du Vendredi Fou',
        giveaway_banner_title: 'Concours du Vendredi Fou',
        giveaway_banner_subtitle: 'Des prix à gagner à chaque jour!',
        giveaway_banner_date: '18 novembre – 6 décembre 2024',
        giveaway_banner_button_text: 'S’inscrire aux tirages du jour',
        get_app: 'Obtenez Flipp',
        giveaway_christmas_title: 'ConcoursJours de Noël',
        giveaway_christmas_subtitle: 'Prix quotidiens d’une valeur de 500 $',
        giveaway_christmas_button_text: 'Participez maintenant',

        giveaway_back_to_school_title1: 'Concours',
        giveaway_back_to_school_title2: 'La rentrée scolaire',
        giveaway_back_to_school_date: 'Du 21 au 24 août',
        giveaway_back_to_school_description: 'Jusqu’à 2 000 $ CA en prix!',

        // Proudly Canadian deals
        proudly_canadian_banner_title:
            'Économisez chez vos magasins préférés, fièrement canadiens',
        proudly_canadian_banner_title1: 'Économisez chez vos magasins',
        proudly_canadian_banner_title2: 'préférés, fièrement canadiens',
        proudly_canadian_deals: 'Découvrez les offres',
        proudly_canadian_disclaimer:
            'Nous nous sommes fiés sur des données fournies par un tiers de confiance pour mettre en vedette les entreprises canadiennes ou les produits fabriqués au Canada. Nous n’avons toutefois pas vérifié de façon indépendante la structure de propriété et nous ne pouvons pas garantir l’exactitude des données. Nous vous prions de mener votre propre recherche.',

        // Watchlist Page
        watchlist_title:
            'La liste de suivi n’est offerte que sur l’application Flipp.',
        watchlist_headline:
            'Constatez les chutes de prix grâce à la liste de suivi de Flipp. Seulement sur l’application Flipp',
        watchlist_button: 'Télécharger l’application',

        // Landing Page strings ---------->
        about_flipp: 'À propos',
        give_feedback: 'Donnez votre avis pour remporter des prix',
        flipp_mobile: 'Application mobile Flipp',
        landing_page_header: 'Épargnez davantage chaque semaine avec Flipp',
        landing_page_description:
            'Flipp rend vos achats hebdomadaires facile avec le plus grand nombre de circulaires et des coupons de vos marques préférées.',
        enter_postal_code_error_message:
            'Veuillez entrer un code postal valide.',
        find_weekly_ads: 'Trouvez les circulaires hebdomadaires pour',
        browse_flipp: 'Parcourir Flipp',
        enter_postal_code:
            'Entrez votre code postal et nous trouverons les circulaires hebdomaires dans votre région.',
        how_it_works: 'Comment ça fonctionne?',
        browse: 'Parcourir',
        browse_description:
            'Parcourez des centaines de circulaires hebdomadaires ',
        search_description:
            'Trouvez un magasin, article ou marque spécifique facilement',
        clip: 'Ajouter',
        clip_description:
            'Appuyez sur un article pour l’ajouter et rester organisé.',
        flyers_found: (c) =>
            `Nous avons trouvé ${c['num']} circulaires de vos détaillants préférés dans votre région`,
        browse_flyer: 'Sélectionnez une circulaire et commencez à explorer.',
        more_flyers: (c) =>
            `Voir ${c['num']} autres circulaires dans la région de ${c['city']}`,
        all_flyers: (c) =>
            `Voir tous les circulaires dans la région de ${c['city']}`,
        search_value:
            '{#1}Cherchez{\\1} des articles, des marques et des magasins pour trouver rapidement ce dont vous avez besoin.',
        find_deals_for: 'Trouvez-moi les meilleures aubaines pour ',
        search_flipp: 'Chercher Flipp',
        has_mobile: 'Nous avons une application mobile.',
        mobile_title:
            "Accédez aux dernières aubaines au bout des doigts en tout temps avec l'application mobile Flipp pour iOS ou Android",
        mobile_title_new:
            'Économisez à tout moment grâce à {#1}l’application Flipp.{\\1}',
        mobile_value:
            'Ajoutez vos cartes de fidélité, synchronisez votre liste d’achats à vos appareils, et partagez votre liste avec votre partenaire de magasinage – uniquement sur l’application!',
        mobile_feature1:
            '{#1}Synchronisez facilement{\\1} votre liste d’achats pour la partager.',
        mobile_feature2: '{#1}Parcourez la boutique{\\1} en toute facilité.',
        mobile_feature3:
            '{#1}Ajoutez vos cartes de fidélité{\\1} pour profiter d’économies au moment de passer vos commandes.',
        learn_more: 'En savoir plus',
        landing_page_title: "Circulaires, coupons et liste d'achats",
        deal_search: 'Saisissez un article',
        deal_search_desktop: 'Essayez une recherche pour des « œufs »',
        deal_sample1: 'Fromage',
        deal_sample2: 'Couches',
        deal_sample3: 'Yogourt',
        deal_sample4: 'Beurre',
        deal_sample5: 'Poulet',
        read_weekly_ad: 'Voir circulaire hebdomadaire',

        // New Landing Page strings ---------->
        back_to_top: 'Retourner au haut de la page',
        flyers_header_category: 'Circulaires',
        start_saving: 'Économiser dès maintenant',
        new_landing_page_description:
            '{#1}Économisez dès maintenant.{\\1}Flipp vous offre tout ce dont vous avez besoin pour économiser chaque semaine.',
        how_to_save_feature1:
            '{#1}Saisissez votre code postal{\\1} ci-dessous pour voir les dernières offres à proximité.',
        how_to_save_feature2:
            '{#1}Plus de 100 coupons{\\1} de vos marques favorites',
        how_to_save_feature3:
            '{#1}La meilleure liste d’achats{\\1} qui soit pour vous aider à économiser',
        flyers_feature_heading:
            'Économisez auprès de plus de 2 000 {#1}magasins favoris.{\\1}',
        flyers_feature_description1:
            'Flipp est votre marché unique vous permettant d’économiser et de profiter de plusieurs offres. Parcourez, chaque semaine, les circulaires numériques des détaillants à proximité, ou recherchez les articles dont vous avez besoin.',
        flyers_feature_description2_part1: 'Téléchargez l’application ',
        flyers_feature_description2_part2:
            ' dès aujourd’hui pour vivre la meilleure expérience mobile qui soit et rechercher des produits à tout moment. L’application est disponible sur l’App Store ou sur Google Play.',
        flipp_app_title: 'Flipp',
        flyers_feature_cta: 'Parcourir les circulaires',
        coupons_feature_heading:
            'Grâce à Flipp, le {#1}couponnage{\\1} n’a jamais été aussi facile.',
        coupons_feature_description1:
            'Nous avons des coupons des plus grandes marques et des plus importants détaillants qui vous permettront d’économiser de 20 % à 70 % sur une vaste gamme de produits.',
        coupons_feature_description2:
            'Trouvez des coupons et utilisez-les en magasin!',
        coupons_feature_cta: 'Parcourir les coupons',
        sl_feature_heading:
            'Restez organisé grâce à votre {#1}liste d’achats numérique.{\\1}',
        sl_feature_description_part1:
            'Ajoutez des offres directement à votre liste d’achats, ou ajoutez vos propres articles. Flipp trouvera instantanément des offres pour les articles de votre liste. Téléchargez l’application ',
        sl_feature_description_part2:
            ' pour avoir, à portée de main, votre liste pendant que vous magasinez.',
        sl_feature_cta: 'Créer une liste d’achats',

        // Error Page strings ---------->
        missing_page_title: 'Nettoyage en cours dans l’allée 404!',
        missing_page_apology_msg:
            "Nous sommes désolés pour ce dégât. La page que vous tentez d'ouvrir n'a pas été trouvée ou n'est pas disponible dans votre région.",
        missing_page_cta_msg:
            'N’hésitez pas à consulter vos toutes dernières circulaires pendant que nous nettoyons.',
        missing_page_cta_button: 'Retournez aux Circulaires',

        // Footer strings ---------->
        retail_platform: 'Plateforme de vente au détail',
        media_platform: 'Plateforme de médias',
        contact_us: 'Contactez-nous',
        help: 'Aide',
        help_link: 'https://help.flipp.com/hc/fr-ca',
        client_login: 'Accès client',
        terms: "Conditions d'utilisation",
        accessibility: "Plan d'accessibilité",
        ad_choices: 'Choix publicitaires',
        hiring: 'Nous embauchons!',
        blog_desc:
            'Consultez le blogue de Flipp dès aujourd’hui pour obtenir des conseils de magasinage, des inspirations quotidiennes et des idées de projets au budget restreint.',
        read_blog: 'Lire le blogue',
        blog_button: 'Blogue',
        copyright: (c) =>
            `© Copyright ${c['year']} Flipp Corp. Tous droits réservés.`,
        webgl_warning_msg:
            "Flipp utilise une technologie standard appelée WebGL afin d'offrir une expérience visuelle extraordinaire. Nous avons remarqué que vous ne l'avez pas activée.",
        webgl_warning_cta:
            'Activez WebGL dès maintenant pour obtenir la meilleure expérience visuelle possible ou continuez à lire la circulaire ainsi.',
        webgl_warning_click_here: 'Cliquez ici',
        webgl_warning_learn: ' pour découvrir comment activer WebGL.',
        old_browser_warning_msg:
            "Oh non! Votre navigateur n'est pas assez récent!",
        old_browser_latest_tech_msg:
            "Nous avons conçu Flipp à l'aide des plus récentes technologies pour qu'il soit rapide et facile à utiliser.",
        old_browser_cta:
            "Veuillez télécharger l'un de ces navigateurs gratuits et à jour pour profiter de la meilleure expérience possible.",
        runtime_error_title: "Oups! Une erreur s'est produite.",
        runtime_error_msg:
            "Veuillez essayer de réinitialiser la page ou <a href='https://flipp.force.com/Community_HomePage?lang=fr_CA'>veuillez</a> communiquer avec nous si l’erreur persiste.",

        coupons: 'Coupons',
        coupon: 'Coupons',
        coupon_heading_can:
            'Nous avons créé des moyens d’épargner encore plus facilement',
        coupon_sub_heading_can:
            'Connectez-vous pour sauvegarder et synchroniser vos cartes de fidélité à votre compte Flipp. {#1}Téléchargez{\\1} l’application Flipp pour y accéder de partout.',
        coupon_heading_usa:
            "Épargnez d'avantage avec des coupons liés à vos cartes de fidélité",
        coupon_sub_heading_usa:
            'Connectez-vous pour sauvegarder vos cartes de fidélité à votre compte Flipp, télécharger des coupons et synchroniser avec tous vos appareils.',
        coupon_category_notice: (c) => `${i18n(c['category'])} Coupons`,

        available_at: 'Disponible au ',
        see_in_weekly_ad: 'Voir dans le dépliant',
        matchup_at: 'Jumelé chez',
        regular_price: 'Prix rég.',
        sale_price: 'Solde',
        current_price: 'Prix actuel',
        coupon_price: 'Coupon',
        final_price: 'Obtenez-le pour',
        coupon_details_title: 'Détails du coupon',

        no_coupons_for_postal_code: (c) =>
            `Aucun coupons pour '${c['postal_code']}'.`,

        // Autocomplete strings ----->
        autocomplete_separator_merchants: 'Détaillants',
        autocomplete_separator_items: 'Articles',
        autocomplete_flyer: '1 circulaire disponible',
        autocomplete_flyers: (c) => `${c['num']} circulaires disponibles`,

        // Accounts strings ------>
        signin: 'Connectez-vous',
        continue_with_email: "Connexion avec l'e-mail",
        continue_with_google: 'Connexion avec Google',
        continue_with_facebook: 'Connexion avec Facebook',
        signin_action: 'connecter',
        signin_header: 'Se connecter',
        signin_no_account: 'Vous n’avez pas de compte?',
        signin_resend_verification_code: 'Renvoyer le code de vérification',
        signup: 'Inscrivez-vous',
        signup_header: 'Inscrivez-vous',
        signup_have_account: 'Vous avez déjà un compte?',
        enter_email_error: 'Veuillez entrer votre email',
        enter_invalid_email_error: 'Format d’adresse courriel non valide',
        enter_password_error: "S'il vous plait, entrez votre mot de passe",
        enter_previous_password_error:
            'Veuillez saisir votre nouveau mot de passe',
        USER_NOT_CONFIRMED: 'L’utilisateur n’est pas confirmé', // override backend error message
        EMAIL_EXISTS: 'Cette adresse courriel a déjà été utilisée.', // override backend error message
        opt_in_header:
            'Des offres incroyables envoyées directement à votre boîte de réception',
        opt_in_text:
            'Nous vous enverrons des mises à jour et des offres des magasins à proximité. En vous inscrivant pour recevoir les courriels de Flipp, vous acceptez nos modalités. Vous pouvez vous désabonner à tout moment. Pour obtenir plus de renseignements, consultez notre {#1} politique en matière de confidentialité {\\1} ou {#2} communiquez avec nous.{\\2}',
        opt_in_button_text: 'S’inscrire aux courriels',
        no_thanks: 'Non merci',
        enter_confirmation_code_error:
            'Veuillez saisir le code de confirmation',
        confirm_password_error: 'Veuillez confirmer votre mot de passe',
        passwords_mismatch_error: 'les mots de passe doivent correspondre',
        firstname: 'Prénom',
        password: 'Mot de passe',
        new_password: 'Nouveau mot de passe',
        confirm_password: 'Confirmez le mot de passe',
        forgot_my_password: 'J’ai oublié mon mot de passe',
        continue_with: 'Ou continuer avec',
        forgot_password_header: 'Réinitialisez votre mot de passe',
        forgot_password_text:
            'Veuillez entrer votre courriel pour recevoir un code pour réinitialiser un mot de passe',
        forgot_password_send_code: 'Envoyez-moi le code',
        confirm_email_header: 'Veuillez vérifier votre adresse courriel',
        confirm_email_resend_header: 'Veuillez vérifier votre adresse courriel',
        confirm_email_text:
            'Pour vérifier votre compte, veuillez inscrire votre code de vérification ci-dessous.',
        confirm_email_resend_text:
            'Saisissez le code de vérification envoyé à votre adresse e-mail pour terminer la création de votre compte Flipp.',
        confirm_email_code: 'Code de vérification',
        confirm_email_button_text: 'Continuer',
        confirm_email_under_button_text:
            'Vous n’avez pas reçu de code de vérification? Veuillez vérifier votre dossier spam ou courrier indésirable',
        confirm_email_under_button_action: 'L’envoyer à nouveau.',
        maybe_later: 'Peut-être plus tard',
        confirm_change_password_header: 'Merci de consulter vos courriels',
        confirm_change_password_text:
            'Afin de réinitialiser votre mot de passe, veuillez entrer votre code de vérification ci-dessous.',
        change_password: 'Changer le mot de passe',
        change_password_header: 'Changez votre mot de passe',
        change_password_text: '',
        change_password_button_text: 'Confirmer',
        previous_password: 'Mot de passe actuel',
        signout: 'Déconnexion',
        signin_title: 'Créez un compte sur',
        signin_value:
            'En créant un compte, vous pouvez enregistrer vos listes d’achat, vos coupons et vos cartes de fidélité, et y accéder chaque fois que vous ouvrez une session.',
        signin_legal:
            'En ouvrant une session, vous acceptez nos {#1}modalités de service{\\1} et notre {#2}politique de confidentialité{\\2}',
        signin_success: 'Vous avez connecté.',
        signin_email_missing:
            'Flipp souhaiterait accéder à votre adresse courriel.',
        stop_all_the_posting: "Flipp n'affichera aucun contenu en votre nom.",
        name: 'Nom',
        email: 'Courriel',
        email_signup: 'Abonnez-vous au service de courriels',
        email_signup_prop:
            'Souhaitez-vous être au courant des dernières nouvelles et promotions Flipp? Vous pouvez vous désabonner à tout moment.',
        email_decline: 'Refuser',
        email_accept: 'Accepter',
        signin_error:
            'Une erreur est survenue. Veuillez essayer de nouveau plus tard.',
        please: 'Veuillez vous ',

        // Load to card --------->
        my_cards: 'Mes cartes de fidélité',
        add_card: 'Ajoutez une carte de fidélité',
        card_category_all: 'Tout les coupons',
        card_category_card: 'Carte de fidélité',
        card_category_print: 'Imprimer',
        cancel: 'Annuler',
        card_number: 'Numéro de carte',
        phone_number: 'Numéro de téléphone',
        enter_card_number: 'Entrez votre numéro de carte',
        enter_phone_number: 'Entrez votre numéro de téléphone',
        enter_pin: 'Entrez code NIP',
        one_coupon_available: '1 coupon disponible',
        add_card_invalid:
            "Ce numéro de carte n'est pas valide. Veuillez vérifier et essayer de nouveau.",
        add_pin_invalid:
            'Ce code NIP est non valide. Veuillez vérifier et essayer de nouveau.',
        add_phone_invalid:
            "Ce numéro de téléphone n'est pas valide. Veuillez vérifier et essayer de nouveau.",
        multi_coupon_available: (c) => `${c['num']} coupons disponibles`,
        remove_card: 'Enlever cette carte',
        remove_card_text: 'Voulez-vous vraiment enlever cette carte?',
        remove_card_yes: 'Oui',
        removing: 'Suppression...',
        no_coupons_clipped: 'Aucun coupon téléchargé à ce carte',
        clipped: 'Téléchargé',
        redeemed: 'Encaissé',
        clip_cta: 'Téléchargér directement à une carte de fidélité',
        add_card_login_alert:
            'Vous devez vous connecter avant de pouvoir télécharger un coupon',
        add_card_alert: (c) =>
            `Vous devez ajouter votre carte de fidélité ${c['name']} avant de pouvoir télécharger ce coupon`,
        clipping_error: "Coupon n'a pas pu être téléchargé à votre carte",
        add_card_success: () => `Votre carte de fidélité a été ajouté`,
        add_card_error: "Votre carte de fidélité n'a pas pu être ajoutée",
        search_cards: 'Rechercher les cartes de fidélité',
        remove_card_error: "Votre carte de fidélité n'a pas pu être suprimée",
        print_coupons: "Coupons d'impression",
        clipping_help_msg: "Aide avec l'encaissement & le téléchargement",
        unavailable: 'Indisponible',
        unavailable_message: (c) =>
            `Votre carte ${c['card']} n'est pas disponible. Veuillez essayer de nouveau plus tard.`,
        coupons_category: (c) => `${c['name']} Coupons`,
        show_more: 'Montrer plus',
        no_loyalty_programs: 'Aucunes cartes de fidélité trouvées',
        postcard_error_11: "Ce numéro de carte n'est pas valide.",
        postcard_error_12: 'Utilisateur est déjà enregistré au marchand.',
        postcard_error_13:
            'La carte est présentement enregistrée à une autre filiale du marchand.',
        postcard_error_21:
            'Coupon présentement coupé à une autre filiale du marchand.',

        // Shopping List --------->
        sl_share_accept_header: (c) =>
            `${c['name']} vous a invité à partager des listes d'achats`,
        sl_share_accept_footer:
            "Collaborez, organisez & épargnez sur vos listes d'achats en temps réel.",
        sl_share_sign_in: 'Ouvrir une session pour partager nos listes',
        sl_share_no_thanks: 'Non, merci',
        sl_share_combine: 'Combiner nos Listes',
        sl_share_combining: 'Veuillez patienter - nous combinons vos listes...',
        sl_share_combine_complete: (c) =>
            `${c['name']} et vous partagez maintenant une liste d'achats`,
        sl_share_combine_cancel:
            "Les articles et les produits de votre liste d'achats ne seront plus combinés.",
        sl_disconnect_msg: 'Déconnecter la liste d’achats',
        sl_share_view_list:
            "Ouvrez votre liste d'achats partagée pour commencer à collaborer en temps réel.",
        sl_share_error_title: "Cette invitation de partage n'est plus valide",
        sl_share_error_desc:
            "Veuillez aviser l'expéditeur de renvoyer leur liste d'achats",
        sl_share_error_self_title:
            "Vous ne pouvez pas partager une liste d'achats avec vous-même",
        sl_share_error_self_desc:
            "Assurez-vous d'être connecté avec un compte différent afin d'accepter cette invitation de partage.",
        sl_share_error_invite_accepted_title:
            'Vous avez déjà accepté cette invitation',
        sl_share_error_invite_accepted_desc:
            "Vous collaborez déjà sur une liste d'achats. Veuillez télécharger notre application mobile pour débuter.",
        sl_share_error_already_shared_title: "Liste d'achats déjà partagée",
        sl_share_error_already_shared_desc:
            "Cette liste d'achats a déjà le nombre maximum de collaborateurs.",

        shopping_list: "Liste d'achats",
        shopping_list_shared: "Liste d'achats partagée",
        shopping_list_title: "Votre liste d'achats - Flipp",
        shopping_list_cta: 'Découvrir notre nouvelle liste d’achats!',
        enter_item: 'Ajoutez un article',
        zero_state_login_title: "Enregistrez votre liste d'achats",
        zero_state_login_desc:
            "Connectez-vous pour enregistrer votre liste d'achats et pour l'avoir à votre disposition n'importe où.",
        popular_items_header: 'Articles populaires',
        one_week_ago_items_header: 'Ajouté il y a 1 semaine',
        two_weeks_ago_items_header: 'Ajouté il y a 2 semaines',
        recommended_items_header: 'Articles recommandés',
        add_all: 'Ajoutez-les tous',
        no_more_suggestions: 'Vous avez ajouté toutes nos suggestions.',
        no_more_suggestions_cta:
            "Revenez la semaine prochaine pour consulter d'autres recommandations.",
        no_clippings: 'Aucun article pour le moment',
        no_items: 'Aucun produit pour le moment',
        no_clippings_message:
            'Les articles ajoutés à partir des circulaires et de votre liste d’achats se retrouveront ici pour un accès facile',
        deal: 'Aubaine',
        deals: 'Aubaines',
        clean_up: 'Effacer la liste',
        clean_up_title: "Effacer la liste d'achats?",
        clean_up_desc:
            'En effaçant votre liste, vous supprimerez les articles cochés et expirés.',
        clean_up_complete: "Votre liste d'achats a été effacée avec succès.",
        delete_list_title: "Supprimer la liste d'achats?",
        delete_list_desc:
            'Tous les produits dans votre liste seront supprimés, mais non vos articles.',
        delete_list: 'Supprimer la liste',
        shopping_list_starter:
            "Commencez à créer votre liste d'achats en saisissant un produit ou en cliquant sur l'un de ces produits",
        shopping_list_starter_part1: "Commencez à créer votre liste d'achats",
        shopping_list_starter_part2: ' en saisissant un produit',
        shopping_list_starter_part3: ' ou',
        shopping_list_starter_part4: " en cliquant sur l'un de ces produits",
        your_clippings: 'Vos produits',
        no_items_found:
            'Désolé, nous ne sommes pas en mesure de trouver de résultat pour votre produit.',
        item_count_single: 'produit',
        item_count_multi: 'produits',
        flipp_to_go: 'Emportez Flipp avec vous',
        flipp_to_go_desc:
            "Créez votre liste d'achats dans le confort de votre foyer puis apportez-la en magasin.",
        share_list_title: "Partagez votre liste d'achats",
        share_list_desc:
            "Combinez votre liste avec celle d'un ami et collaborez sur une seule liste d'achats",
        share_list_cta: 'Partagez un lien d’invitation',
        sharing_sl_msg: 'Collaboration à votre liste d’achats',
        start_sharing_sl_msg: 'Collaborer à la liste d’achats',
        stop_sharing_sl_msg: 'Cesser la collaboration?',
        invite_dialog_title: 'Commencer à collaborer maintenant',
        invite_dialog_desc:
            "Envoyez un lien privé à un ami. Une fois qu'il aura accepté votre invitation, vos listes d'achats seront combinées.",
        invite_dialog_copy: 'Copiez ce lien privé pour le partager',
        invite_dialog_sharing: "Création de l'invitation...",
        unshare: 'Ne plus partager',
        unshare_dialog_title:
            "Êtes-vous certain de ne plus vouloir partager votre liste d'achats?",
        unshare_dialog_desc:
            "Vous ne serez plus en mesure de collaborer pour vos listes d'achats et articles.",
        unshare_dialog_action: "Ne plus partager la liste d'achats",
        unshare_dialog_message: "Arrêt du partage de la liste d'achats...",
        shopping_list_save_error:
            "Une erreur s'est produite pendant l'enregistrement des changements de votre liste d'achats. Veuillez essayer de nouveau.",
        shopping_list_exceeded_limit_error:
            "Votre liste ne peut pas être enregistrée, car vous avez dépassé le nombre maximal d'articles.",
        shopping_list_blocked:
            "Téléchargez l'application Flipp pour déverrouiller votre liste dachats",
        shopping_list_blocked_bf:
            "Soyez à l'affût du Vendredi fou avec la Liste de suivi. Seulement avec l'appli Flipp.",
        shopping_list_blocked_dist:
            "Visitez notre site web pour commencer à créer votre liste d'achats.",
        embedded_shopping_list_title: "Votre liste d'achats Flipp",
        embedded_shopping_list_desc:
            "Ajoutez des articles à votre liste et synchronisez-la avec votre application Flipp. N'oubliez plus jamais un article!",

        category_my_clippings: 'Ma liste',
        category_other: 'Autre',
        category_photo_lists: 'Listes de photos',

        // Quick Adds -------------->
        item_apple: 'Pomme',
        item_bacon: 'Bacon',
        item_bananas: 'Banane',
        item_bread: 'Pain',
        item_broccoli: 'Brocoli',
        item_butter: 'Beurre',
        item_cereal: 'Céréale',
        item_cheese: 'Fromage',
        item_chicken: 'Poulet',
        item_cucumber: 'Concombre',
        item_eggs: 'Œufs',
        item_grapes: 'Raisin',
        item_yogurt: 'Yogourt',
        item_lettuce: 'Laitue',
        item_milk: 'Lait',
        item_orange_juice: "Jus d'orange",
        item_strawberry: 'Fraise',
        item_toilet_paper: 'Papier hygiénique',
        item_tomato: 'Tomate',
        item_water: 'Eau',
        item_watermelon: "Melon d'eau",

        // Favourites --------->
        fav_page: 'Favoris',
        fav_title: 'Favoris - Flipp',
        fav_h2: 'Vous n’avez pas de magasin favori actuellement.',
        fav_desc:
            'Cliquez sur le [HEART] de vos magasins favoris pour voir leurs circulaires dans vos favoris.',

        // Common strings --------->
        ok: 'OK',
        ok_got_it: 'OK', // "Got It" is too long in French.
        got_it: 'Compris',
        copy: 'Copie',
        session_expired_title: 'Votre session a expiré',
        session_expired_desc: 'Connectez-vous pour continuer.',

        // Breadcrumbs
        br_home: 'Canada',

        // Expiread Metadata
        expired_flyer_title: (c) =>
            `Circulaire - ${c['name']} - ${c['city']}, ${c['state']}`,
        expired_flyer_description: (c) =>
            `Cette circulaire de ${c['name']} de la ville de ${c['city']}, ${c['state']}, a expiré . Elle a été remplacée par une nouvelle circulaire de ${c['name']}`,
        expired_flyer_h1: (c) =>
            `Circulaire ${c['name']} - ${c['city']}, ${c['state']} avec id ${c['id']} - Expiré`,
        expired_flyer_body: (c) =>
            `Cette circulaire de ${c['name']}, ${c['id']} de la ville de ${c['city']}, ${c['state']} a expiré. Une nouvelle circulaire est disponible. Les utilisateurs à la recherche de circulaires de ${c['name']} à ${c['city']}, seront redirigés vers la circulaire la plus récente.`,

        // Expiread Metadata Item
        expired_item_title: (c) => `${c['name']} - ${c['city']}, ${c['state']}`,
        expired_item_description: (c) =>
            `Cet article de circulaire - ${c['name']} - dans la ville de ${c['city']}, ${c['state']}. L’item - ${c['name']} - a expiré `,
        expired_item_h1: (c) =>
            `${c['name']} ${c['id']} Article de circulaire - ${c['city']}, ${c['state']} Expiré `,
        expired_item_body: (c) =>
            `Cet article de circulaire - ${c['name']} ${c['id']} - était disponible à ${c['city']}, ${c['state']}. L'article - ${c['name']} - a expiré l. Puisqu'il n'y a pas de nouvel article de disponible pour le même détaillant à ${c['city']}, nous redirigeons les utilisateurs vers la page des circulaires les plus à jour.`,

        // Expiread Metadata Coupon
        expired_coupon_title: (c) =>
            `Coupon - ${c['name']} - ${c['city']}, ${c['state']} - #${c['id']}`,
        expired_coupon_description: (c) =>
            `Ce coupon - ${c['name']} -  numéro ${c['id']} de la ville de ${c['city']}, ${c['state']} a expiré. Les utilisateurs sont redirigés vers le contenu le plus pertinent.`,
        expired_coupon_h1: (c) =>
            `Coupon - ${c['name']} - Numéro ${c['id']} in ${c['city']}, ${c['state']} - Expiré `,
        expired_coupon_body: (c) =>
            `Ce coupon - ${c['name']} - au numéro d'identification ${c['id']} de la ville de ${c['city']}, ${c['state']} a expiré. Puisqu'il n'y a pas de nouveau coupon correspondant à - ${c['name']} - #${c['id']} du même détaillant à ${c['city']}, les utilisateurs sont redirigés vers la page énumérant les dernières circulaires, articles et coupons de ${c['name']}.`,

        // Mobile Bananer
        mobile_popup_banner:
            'Consultez cette circulaire dans l’application Flipp pour vivre la meilleure expérience de lecture qui soit.',
        continue_in_web_browser: 'Continuer sur le navigateur Web',

        flyersCountry_title: 'Circulaires de magasins au Canada',
        storeCityFlyers_title:
            'Parcourez les circulaires des magasins par ville',
        flyersCountry_content1:
            'Avec notre collection de circulaires canadiennes, Flipp vous apporte toutes les dernières soldes et promotions des détaillants à travers le Canada.',
        flyersCountry_content2:
            "Pour vous servir à magasiner intelligemment, Flipp est toujours à jour avec les plus récentes infos sur les circulaires. Chaque fois que de nouvelles circulaires sont mises en circulation, Flipp vous aide à découvrir les façons inégalables d'économiser sur pratiquement n'importe quel achat. Que vous procuriez des vêtements, des meubles, des produits d'épicerie, des jouets, de la nourriture, des fournitures scolaires ou de la décoration, Flipp vous simplifie trouver ce dont vous avez besoin au meilleur prix.",
        flyersCountry_content3:
            'Notre collection de circulaires hebdomadaires des principaux magasins canadiens, dont Canadian Tire, Princess Auto, Sport Chek, Brault & Martineau, Toys "R" Us, Tigre Géant et plus, vous permet de profiter de bonnes affaires! Toutes les annonces hebdomadaires sont accessibles sur notre site Web, mais si vous voulez obtenir les rabais au bout des doigts, téléchargez l\'appli Flipp dès aujourd\'hui.',
        flyersCountry_content4_a: 'Disponible pour les appareils ',
        flyersCountry_content4_or: 'ou',
        flyersCountry_content4_b:
            ", l'appli Flipp est l'un des outils d’épargne les plus puissants que vous puissiez trouver. Réunissant tout, des circulaires sur l'électronique aux coupons d'épicerie, l'appli Flipp vous permet de repérer et de comparer rapidement les prix d'un magasin à l'autre. Rester organisé avec une liste d'achats, et lorsque vous vous connectez, votre compte se synchronisera automatiquement sur tous vos appareils. Vous n'avez donc jamais à vous soucier d'oublier votre liste d'achats avec vos énormes économies.",
        flyersCountry_content5:
            "Où que vous soyez au Canada, les circulaires et les coupons sont un moyen efficace de garder plus d'argent dans vos poches. Notre collection de circulaires canadiennes comprend des entreprises canadiennes de toutes tailles et ne laisse aucun code postal de côté.",
        flyersCountry_top10: '10 premières villes',
        flyersCountry_footnote:
            'Remarque : tous les logos et marques déposés sont la propriété de leurs propriétaires respectifs.',
        flyersCountry_all: 'Toutes les villes',

        flyer_title: (c) => `Circulaire ${c['storeName']}`,
        flyer_location_title: (c) => `Circulaires ${c['city']}`,

        footer_yourstory: 'Votre Histoire',
        footer_company: 'Compagnie',
        footer_blog: 'Blogue',
        footer_retailers: 'Détaillants',
        footer_locations: 'Emplacements',
        footer_careers: 'Carrière',
        footer_connect: 'Pour nous joindre',
        footer_email: 'email',
        footer_facebook: 'facebook',
        footer_twitter: 'twitter',
        footer_instagram: 'instagram',
        footer_contact: 'Adresse postale',
        footer_copyright: '© Copyright 2023 Flipp Corp. Tous droits réservés.',
        footer_browseFlyersByCityAndStore:
            'Parcourez les circulaires par ville et magasin',
        footer_browseFlyersByCity: 'Parcourez les circulaires par ville',

        footerAbout: 'À propos',
        footerForRetailers: 'Détaillants',
        footerCareers: 'Carrières',
        footerStores: 'Magasins',
        footerBlog: 'Blogue',
        footerCopyright: '\u00A9 {{year}} reebee Inc., Tous droits réservés.',
        footerTerms: 'Termes',
        footerPrivacy: 'Confidentialité',
        footerCanadianFlyers: 'Circulaires canadiennes',

        footerStoresFlyers: 'Circulaires des magasins',
        footerStoresFlyersByCity: 'Circulaires de magasins par ville',
        footerCountryCityFlyers: 'Circulaires de villes canadiennes',

        store_title: (c) => `Circulaire ${c['storeName']} en Ligne`,
        store_location_title: (c) =>
            `Circulaires de ${c['storeName']} à ${c['city']}`,
        store_no_location_title: (c) => `Circulaires de ${c['storeName']}`,
        store_blackfriday_promo: (c) =>
            `La circulaire du Vendredi Fou de ${c['storeName']} est arrivée!`,
        store_currentFlyers: (c) => `Circulaires ${c['storeName']} en cours`,
        store_upcomingFlyers: (c) => `Circulaires ${c['storeName']} à venir`,
        store_popularFlyers: 'Circulaires populaires',
        store_popularCityFlyers: (c) => `Circulaires populaires à ${c['city']}`,
        store_storeCityFlyers: (c) =>
            `${c['store']} magasins dans d'autres villes`,
        store_aboutStore: (c) => `À propos de ${c['store']}`,
        store_aboutFlipp_title: 'À propos de Flipp',
        store_noFlyers: (c) =>
            `Désolé, il n'y a actuellement aucune circulaire pour ce ${c['storeName']} à ${c['city']}`,
        store_noFlyers_noCity: (c) =>
            `Désolé, il n'y a actuellement aucune circulaire pour ce ${c['storeName']}`,
        store_aboutFlipp_text:
            'Parcourez les circulaires, recherchez des produits et synchronisez votre liste de courses avec Flipp.',
        store_seoThumbnail: (c) =>
            `${c['storeName']} Flyer à ${c['city']} qui propose des offres dans la catégorie ${c['category']}`,
        upcoming: 'À venir',
        downloadAppBanner_promo:
            'Consultez les dernières circulaires sur votre cellulaire!',
        downloadAppBanner_qr: 'Essayez-le gratuitement maintenant',
        openFlyer: 'Ouvrir la circulaire',
        helpCenter: "Centre d'aide",
        home: 'Accueil',
        seeBlackFridayDeals: 'Voir les offres du Black Friday',

        // Reebee integration
        welcome_to_flipp_title: () => 'Bienvenue chez Flipp!',
        welcome_to_flipp_button: () => "J'ai compris",
        welcome_to_flipp_text: () =>
            "Nous sommes ravis de vous accueillir. Jetez un coup d'œil et découvrez notre contenu et nos fonctionnalités. Nous espérons que vous l'apprécierez.",
        'help-reebee': () =>
            'https://help.flipp.com/quest-ce-qui-se-passe-avec-reebee-com',
        'reebee-banner': () =>
            "Merci de votre visite. reebee est maintenant alimenté par Flipp. Jetez un coup d'œil!",

        // SDE footer
        powered_by_flipp: 'Produit par Flipp',
        patent: 'Brevets',
    },
}

export function getLanguage() {
    const locale = SeoService.getInstance().locale || 'en'
    return locale.split('-')[0]
}

export function getLocaleFromUrl(): string | null {
    const seoService = SeoService.getInstance()
    const localeFromUrl = window.location.pathname.split('/').filter(Boolean)[0]
    const { locale } = parseSearchParams(new URL(window.location.href))
    if (seoService.isLocale(localeFromUrl)) {
        return localeFromUrl
    }

    if (locale) {
        return locale
    }

    return null
}

function getDictLocale() {
    const locale = SeoService.getInstance().locale
    if (locale === 'en-ca') {
        return locale
    }

    return locale ? locale.split('-')[0] : 'en'
}

export function i18n(key: string, context?: TranslationContext): string {
    const time = DateTimeService.getInstance()
    const locale = getDictLocale()
    time.setLocale(locale)
    const fallbackLocale: string = 'en'

    const dict = translations[locale] || {}
    const fallbackDict = translations[fallbackLocale] || {}
    const value =
        dict[key] ||
        fallbackDict[key] ||
        function (context) {
            console.warn(
                'Missing translation key:',
                key,
                'for lang:',
                locale,
                'with context:',
                context,
                'and dictionary:',
                dict
            )
            return ''
        }
    if (typeof value === 'string') return value
    else return value(context)
}

export function i18nForLocale(
    key: string,
    locale: string,
    context?: TranslationContext
) {
    const dict = translations[locale] || {}
    const value =
        dict[key] ||
        function (context) {
            console.warn(
                'Missing translation key:',
                key,
                'for lang:',
                locale,
                'with context:',
                context,
                'and dictionary:',
                dict
            )
            return ''
        }

    if (typeof value === 'string') return value
    else return value(context)
}

export function ll(date: Date) {
    if (!date) return null
    return DateTimeService.getInstance().formatDate(date, 'MMM d, y')
}

export function friendlyValidity(from: Date, to: Date) {
    const locale = getDictLocale()
    const dtService = DateTimeService.getInstance()
    const nowDay = dtService.fromJsDate(new Date())
    const fromDay = dtService.fromJsDate(from)
    const untilDay = dtService.fromJsDate(to)

    if (nowDay < fromDay) {
        const duration = dtService.getDuration(fromDay, nowDay)
        const days = dtService.getExactDuration(duration, 'days')
        const isSameDay = days < 1 && nowDay.day === fromDay.day
        const isNextDay =
            days < 2 && Math.abs(nowDay.weekday - fromDay.weekday) === 1
        if (isSameDay) {
            return i18n('starts_today', {})
        } else if (isNextDay) {
            return i18n('starts_tomorrow', {})
        } else if (days < 7) {
            return i18n('starts_on_day', {
                day: dtService.formatDate(from, 'EEEE', locale),
            })
        } else {
            return i18n('starts_on_date', {
                date: dtService.formatDate(from, 'MMM d, y', locale),
            })
        }
    } else if (nowDay < untilDay) {
        const duration = dtService.getDuration(untilDay, nowDay)
        const years = dtService.getExactDuration(duration, 'years')
        const months = dtService.getExactDuration(duration, 'months')
        const days = dtService.getExactDuration(duration, 'days')
        const weeks = days / 7

        if (years > 1) {
            if (years < 2) {
                return i18n('1_year_left', {})
            } else {
                return i18n('n_years_left', {
                    n: Math.floor(years).toString(),
                })
            }
        } else if (months > 1) {
            if (months < 2) {
                return i18n('1_month_left', {})
            } else {
                return i18n('n_months_left', {
                    n: Math.floor(months).toString(),
                })
            }
        } else if (weeks > 1) {
            if (weeks < 2) {
                return i18n('1_week_left', {})
            } else {
                return i18n('n_weeks_left', {
                    n: Math.floor(days / 7).toString(),
                })
            }
        } else {
            const isSameDay = days < 1 && nowDay.day === untilDay.day
            const isNextDay =
                days < 2 && Math.abs(nowDay.weekday - untilDay.weekday) === 1
            if (isSameDay) {
                return i18n('ends_today', {})
            } else if (isNextDay) {
                return i18n('until_tomorrow', {})
            } else {
                return i18n('until_day', {
                    day: dtService.formatDate(to, 'EEEE', locale),
                })
            }
        }
    } else {
        return i18n('ended_on_date', {
            date: dtService.formatDate(to, 'MMM d, y', locale),
        })
    }
}

export function removeDiacritics(str: string) {
    const defaultDiacriticsRemovalMap = [
        {
            base: 'A',
            letters:
                /[\u0041\u24B6\uFF21\u00C0\u00C1\u00C2\u1EA6\u1EA4\u1EAA\u1EA8\u00C3\u0100\u0102\u1EB0\u1EAE\u1EB4\u1EB2\u0226\u01E0\u00C4\u01DE\u1EA2\u00C5\u01FA\u01CD\u0200\u0202\u1EA0\u1EAC\u1EB6\u1E00\u0104\u023A\u2C6F]/g,
        },
        { base: 'AA', letters: /[\uA732]/g },
        { base: 'AE', letters: /[\u00C6\u01FC\u01E2]/g },
        { base: 'AO', letters: /[\uA734]/g },
        { base: 'AU', letters: /[\uA736]/g },
        { base: 'AV', letters: /[\uA738\uA73A]/g },
        { base: 'AY', letters: /[\uA73C]/g },
        {
            base: 'B',
            letters:
                /[\u0042\u24B7\uFF22\u1E02\u1E04\u1E06\u0243\u0182\u0181]/g,
        },
        {
            base: 'C',
            letters:
                /[\u0043\u24B8\uFF23\u0106\u0108\u010A\u010C\u00C7\u1E08\u0187\u023B\uA73E]/g,
        },
        {
            base: 'D',
            letters:
                /[\u0044\u24B9\uFF24\u1E0A\u010E\u1E0C\u1E10\u1E12\u1E0E\u0110\u018B\u018A\u0189\uA779]/g,
        },
        { base: 'DZ', letters: /[\u01F1\u01C4]/g },
        { base: 'Dz', letters: /[\u01F2\u01C5]/g },
        {
            base: 'E',
            letters:
                /[\u0045\u24BA\uFF25\u00C8\u00C9\u00CA\u1EC0\u1EBE\u1EC4\u1EC2\u1EBC\u0112\u1E14\u1E16\u0114\u0116\u00CB\u1EBA\u011A\u0204\u0206\u1EB8\u1EC6\u0228\u1E1C\u0118\u1E18\u1E1A\u0190\u018E]/g,
        },
        { base: 'F', letters: /[\u0046\u24BB\uFF26\u1E1E\u0191\uA77B]/g },
        {
            base: 'G',
            letters:
                /[\u0047\u24BC\uFF27\u01F4\u011C\u1E20\u011E\u0120\u01E6\u0122\u01E4\u0193\uA7A0\uA77D\uA77E]/g,
        },
        {
            base: 'H',
            letters:
                /[\u0048\u24BD\uFF28\u0124\u1E22\u1E26\u021E\u1E24\u1E28\u1E2A\u0126\u2C67\u2C75\uA78D]/g,
        },
        {
            base: 'I',
            letters:
                /[\u0049\u24BE\uFF29\u00CC\u00CD\u00CE\u0128\u012A\u012C\u0130\u00CF\u1E2E\u1EC8\u01CF\u0208\u020A\u1ECA\u012E\u1E2C\u0197]/g,
        },
        { base: 'J', letters: /[\u004A\u24BF\uFF2A\u0134\u0248]/g },
        {
            base: 'K',
            letters:
                /[\u004B\u24C0\uFF2B\u1E30\u01E8\u1E32\u0136\u1E34\u0198\u2C69\uA740\uA742\uA744\uA7A2]/g,
        },
        {
            base: 'L',
            letters:
                /[\u004C\u24C1\uFF2C\u013F\u0139\u013D\u1E36\u1E38\u013B\u1E3C\u1E3A\u0141\u023D\u2C62\u2C60\uA748\uA746\uA780]/g,
        },
        { base: 'LJ', letters: /[\u01C7]/g },
        { base: 'Lj', letters: /[\u01C8]/g },
        {
            base: 'M',
            letters: /[\u004D\u24C2\uFF2D\u1E3E\u1E40\u1E42\u2C6E\u019C]/g,
        },
        {
            base: 'N',
            letters:
                /[\u004E\u24C3\uFF2E\u01F8\u0143\u00D1\u1E44\u0147\u1E46\u0145\u1E4A\u1E48\u0220\u019D\uA790\uA7A4]/g,
        },
        { base: 'NJ', letters: /[\u01CA]/g },
        { base: 'Nj', letters: /[\u01CB]/g },
        {
            base: 'O',
            letters:
                /[\u004F\u24C4\uFF2F\u00D2\u00D3\u00D4\u1ED2\u1ED0\u1ED6\u1ED4\u00D5\u1E4C\u022C\u1E4E\u014C\u1E50\u1E52\u014E\u022E\u0230\u00D6\u022A\u1ECE\u0150\u01D1\u020C\u020E\u01A0\u1EDC\u1EDA\u1EE0\u1EDE\u1EE2\u1ECC\u1ED8\u01EA\u01EC\u00D8\u01FE\u0186\u019F\uA74A\uA74C]/g,
        },
        { base: 'OI', letters: /[\u01A2]/g },
        { base: 'OO', letters: /[\uA74E]/g },
        { base: 'OU', letters: /[\u0222]/g },
        {
            base: 'P',
            letters:
                /[\u0050\u24C5\uFF30\u1E54\u1E56\u01A4\u2C63\uA750\uA752\uA754]/g,
        },
        { base: 'Q', letters: /[\u0051\u24C6\uFF31\uA756\uA758\u024A]/g },
        {
            base: 'R',
            letters:
                /[\u0052\u24C7\uFF32\u0154\u1E58\u0158\u0210\u0212\u1E5A\u1E5C\u0156\u1E5E\u024C\u2C64\uA75A\uA7A6\uA782]/g,
        },
        {
            base: 'S',
            letters:
                /[\u0053\u24C8\uFF33\u1E9E\u015A\u1E64\u015C\u1E60\u0160\u1E66\u1E62\u1E68\u0218\u015E\u2C7E\uA7A8\uA784]/g,
        },
        {
            base: 'T',
            letters:
                /[\u0054\u24C9\uFF34\u1E6A\u0164\u1E6C\u021A\u0162\u1E70\u1E6E\u0166\u01AC\u01AE\u023E\uA786]/g,
        },
        { base: 'TZ', letters: /[\uA728]/g },
        {
            base: 'U',
            letters:
                /[\u0055\u24CA\uFF35\u00D9\u00DA\u00DB\u0168\u1E78\u016A\u1E7A\u016C\u00DC\u01DB\u01D7\u01D5\u01D9\u1EE6\u016E\u0170\u01D3\u0214\u0216\u01AF\u1EEA\u1EE8\u1EEE\u1EEC\u1EF0\u1EE4\u1E72\u0172\u1E76\u1E74\u0244]/g,
        },
        {
            base: 'V',
            letters: /[\u0056\u24CB\uFF36\u1E7C\u1E7E\u01B2\uA75E\u0245]/g,
        },
        { base: 'VY', letters: /[\uA760]/g },
        {
            base: 'W',
            letters:
                /[\u0057\u24CC\uFF37\u1E80\u1E82\u0174\u1E86\u1E84\u1E88\u2C72]/g,
        },
        { base: 'X', letters: /[\u0058\u24CD\uFF38\u1E8A\u1E8C]/g },
        {
            base: 'Y',
            letters:
                /[\u0059\u24CE\uFF39\u1EF2\u00DD\u0176\u1EF8\u0232\u1E8E\u0178\u1EF6\u1EF4\u01B3\u024E\u1EFE]/g,
        },
        {
            base: 'Z',
            letters:
                /[\u005A\u24CF\uFF3A\u0179\u1E90\u017B\u017D\u1E92\u1E94\u01B5\u0224\u2C7F\u2C6B\uA762]/g,
        },
        {
            base: 'a',
            letters:
                /[\u0061\u24D0\uFF41\u1E9A\u00E0\u00E1\u00E2\u1EA7\u1EA5\u1EAB\u1EA9\u00E3\u0101\u0103\u1EB1\u1EAF\u1EB5\u1EB3\u0227\u01E1\u00E4\u01DF\u1EA3\u00E5\u01FB\u01CE\u0201\u0203\u1EA1\u1EAD\u1EB7\u1E01\u0105\u2C65\u0250]/g,
        },
        { base: 'aa', letters: /[\uA733]/g },
        { base: 'ae', letters: /[\u00E6\u01FD\u01E3]/g },
        { base: 'ao', letters: /[\uA735]/g },
        { base: 'au', letters: /[\uA737]/g },
        { base: 'av', letters: /[\uA739\uA73B]/g },
        { base: 'ay', letters: /[\uA73D]/g },
        {
            base: 'b',
            letters:
                /[\u0062\u24D1\uFF42\u1E03\u1E05\u1E07\u0180\u0183\u0253]/g,
        },
        {
            base: 'c',
            letters:
                /[\u0063\u24D2\uFF43\u0107\u0109\u010B\u010D\u00E7\u1E09\u0188\u023C\uA73F\u2184]/g,
        },
        {
            base: 'd',
            letters:
                /[\u0064\u24D3\uFF44\u1E0B\u010F\u1E0D\u1E11\u1E13\u1E0F\u0111\u018C\u0256\u0257\uA77A]/g,
        },
        { base: 'dz', letters: /[\u01F3\u01C6]/g },
        {
            base: 'e',
            letters:
                /[\u0065\u24D4\uFF45\u00E8\u00E9\u00EA\u1EC1\u1EBF\u1EC5\u1EC3\u1EBD\u0113\u1E15\u1E17\u0115\u0117\u00EB\u1EBB\u011B\u0205\u0207\u1EB9\u1EC7\u0229\u1E1D\u0119\u1E19\u1E1B\u0247\u025B\u01DD]/g,
        },
        { base: 'f', letters: /[\u0066\u24D5\uFF46\u1E1F\u0192\uA77C]/g },
        {
            base: 'g',
            letters:
                /[\u0067\u24D6\uFF47\u01F5\u011D\u1E21\u011F\u0121\u01E7\u0123\u01E5\u0260\uA7A1\u1D79\uA77F]/g,
        },
        {
            base: 'h',
            letters:
                /[\u0068\u24D7\uFF48\u0125\u1E23\u1E27\u021F\u1E25\u1E29\u1E2B\u1E96\u0127\u2C68\u2C76\u0265]/g,
        },
        { base: 'hv', letters: /[\u0195]/g },
        {
            base: 'i',
            letters:
                /[\u0069\u24D8\uFF49\u00EC\u00ED\u00EE\u0129\u012B\u012D\u00EF\u1E2F\u1EC9\u01D0\u0209\u020B\u1ECB\u012F\u1E2D\u0268\u0131]/g,
        },
        { base: 'j', letters: /[\u006A\u24D9\uFF4A\u0135\u01F0\u0249]/g },
        {
            base: 'k',
            letters:
                /[\u006B\u24DA\uFF4B\u1E31\u01E9\u1E33\u0137\u1E35\u0199\u2C6A\uA741\uA743\uA745\uA7A3]/g,
        },
        {
            base: 'l',
            letters:
                /[\u006C\u24DB\uFF4C\u0140\u013A\u013E\u1E37\u1E39\u013C\u1E3D\u1E3B\u017F\u0142\u019A\u026B\u2C61\uA749\uA781\uA747]/g,
        },
        { base: 'lj', letters: /[\u01C9]/g },
        {
            base: 'm',
            letters: /[\u006D\u24DC\uFF4D\u1E3F\u1E41\u1E43\u0271\u026F]/g,
        },
        {
            base: 'n',
            letters:
                /[\u006E\u24DD\uFF4E\u01F9\u0144\u00F1\u1E45\u0148\u1E47\u0146\u1E4B\u1E49\u019E\u0272\u0149\uA791\uA7A5]/g,
        },
        { base: 'nj', letters: /[\u01CC]/g },
        {
            base: 'o',
            letters:
                /[\u006F\u24DE\uFF4F\u00F2\u00F3\u00F4\u1ED3\u1ED1\u1ED7\u1ED5\u00F5\u1E4D\u022D\u1E4F\u014D\u1E51\u1E53\u014F\u022F\u0231\u00F6\u022B\u1ECF\u0151\u01D2\u020D\u020F\u01A1\u1EDD\u1EDB\u1EE1\u1EDF\u1EE3\u1ECD\u1ED9\u01EB\u01ED\u00F8\u01FF\u0254\uA74B\uA74D\u0275]/g,
        },
        { base: 'oi', letters: /[\u01A3]/g },
        { base: 'ou', letters: /[\u0223]/g },
        { base: 'oo', letters: /[\uA74F]/g },
        {
            base: 'p',
            letters:
                /[\u0070\u24DF\uFF50\u1E55\u1E57\u01A5\u1D7D\uA751\uA753\uA755]/g,
        },
        { base: 'q', letters: /[\u0071\u24E0\uFF51\u024B\uA757\uA759]/g },
        {
            base: 'r',
            letters:
                /[\u0072\u24E1\uFF52\u0155\u1E59\u0159\u0211\u0213\u1E5B\u1E5D\u0157\u1E5F\u024D\u027D\uA75B\uA7A7\uA783]/g,
        },
        {
            base: 's',
            letters:
                /[\u0073\u24E2\uFF53\u00DF\u015B\u1E65\u015D\u1E61\u0161\u1E67\u1E63\u1E69\u0219\u015F\u023F\uA7A9\uA785\u1E9B]/g,
        },
        {
            base: 't',
            letters:
                /[\u0074\u24E3\uFF54\u1E6B\u1E97\u0165\u1E6D\u021B\u0163\u1E71\u1E6F\u0167\u01AD\u0288\u2C66\uA787]/g,
        },
        { base: 'tz', letters: /[\uA729]/g },
        {
            base: 'u',
            letters:
                /[\u0075\u24E4\uFF55\u00F9\u00FA\u00FB\u0169\u1E79\u016B\u1E7B\u016D\u00FC\u01DC\u01D8\u01D6\u01DA\u1EE7\u016F\u0171\u01D4\u0215\u0217\u01B0\u1EEB\u1EE9\u1EEF\u1EED\u1EF1\u1EE5\u1E73\u0173\u1E77\u1E75\u0289]/g,
        },
        {
            base: 'v',
            letters: /[\u0076\u24E5\uFF56\u1E7D\u1E7F\u028B\uA75F\u028C]/g,
        },
        { base: 'vy', letters: /[\uA761]/g },
        {
            base: 'w',
            letters:
                /[\u0077\u24E6\uFF57\u1E81\u1E83\u0175\u1E87\u1E85\u1E98\u1E89\u2C73]/g,
        },
        { base: 'x', letters: /[\u0078\u24E7\uFF58\u1E8B\u1E8D]/g },
        {
            base: 'y',
            letters:
                /[\u0079\u24E8\uFF59\u1EF3\u00FD\u0177\u1EF9\u0233\u1E8F\u00FF\u1EF7\u1E99\u1EF5\u01B4\u024F\u1EFF]/g,
        },
        {
            base: 'z',
            letters:
                /[\u007A\u24E9\uFF5A\u017A\u1E91\u017C\u017E\u1E93\u1E95\u01B6\u0225\u0240\u2C6C\uA763]/g,
        },
    ]

    for (let i = 0; i < defaultDiacriticsRemovalMap.length; i++) {
        str = str.replace(
            defaultDiacriticsRemovalMap[i].letters,
            defaultDiacriticsRemovalMap[i].base
        )
    }

    return str
}

export const CATEGORIES_FLYER =
    "All Flyers,Black Friday,Proudly Canadian,Boxing Day,Boxing Week,Halloween,Thanksgiving,Cyber Monday,Cyber Week,Holiday Gifting,Christmas,New Year's Eve,New Years,Valentine's Day,President's Day,Easter,Spring,Mother's Day,Father's Day,Memorial Day,Victoria Day,Back to School,Labor Day,Garden & Patio,Groceries,Restaurants,Home & Garden,Pharmacy,General Merchandise,Electronics,Baby & Kids,Fashion,Automotive,Sporting Goods,Pets,Office,Specialty,Buy Online,New Stores,Popular Near You"

export const CATEGORIES_COUPON =
    'All Deals,Household,Grocery,Health & Beauty,Baby,Pets,Other'
